// 19-oct-2026 · Control de pagos de apartados (P5) · las pantallas.
//
// Qué resuelve: hoy el abono de un apartado vive en un comprobante del chat y nadie lo descuenta al
// entregar. Con esto la vendedora (o Isabela) REPORTA el pago, quien tiene el permiso "Validar pagos de
// apartados" (María) lo VALIDA contra el banco y lo reparte entre los folios de la clienta, la caja lo
// registra como ticket ANTICIPO y, al entregar, el ticket lleva la línea "ANTICIPO APLICADO −$X".
//
// Reglas de esta pantalla (spec §8):
// - Solo pinta y pregunta. El dinero lo decide el servidor (captura + libro del bot): aquí nada se da
//   por pagado; "Listo (AB-…)" siempre aclara que todavía NO es pago confirmado.
// - Dinero en CENTAVOS enteros: dos montos son iguales si sus centavos son iguales.
// - Cada componente trae sus hooks adentro (patrón EnviarAWA.jsx): App-merged.jsx solo los monta, y
//   así ningún hook queda después del primer return de App() (pantalla en blanco del 5-oct).
// - CSS propio con prefijo .pa- y los tokens del Design System 2.0 (CSS2): se ve igual en el panel
//   viejo (solo carga CSS) y en el 2.0 (CSS2+CSS3). z-index 10020+: encima del panel viejo (9999) y de
//   .c3-top (10005).
// - Los archivos (comprobantes) se ven SIEMPRE con fetch + token + blob (comprobante.js/verArchivo).
// - Con ANTICIPOS_ACTIVOS=0 el servidor contesta 503 APAGADO y el contador viene en ceros: no sale
//   ningún aviso y la entrega se hace exactamente como hoy (sin cuerpo de anticipo).
import React from 'react';
import { createPortal } from 'react-dom';
import { comprimirComprobante, verArchivo } from './comprobante.js';
const { useState, useEffect, useRef, useCallback } = React;

const BASE = '/api/apartados-pagos';

// ── dinero en centavos ──
export const cents = (x) => { const n = Number(x); return isFinite(n) ? Math.round(n * 100) : 0; };
const pesos = (c) => Math.round(c) / 100;
const $c = (c) => {
  const v = Math.round(Number(c) || 0), a = Math.abs(v), ent = Math.floor(a / 100), cen = a % 100;
  return (v < 0 ? '−' : '') + '$' + ent.toLocaleString('es-MX') + (cen ? '.' + String(cen).padStart(2, '0') : '');
};
const $p = (x) => $c(cents(x));
// "3,000.50" / "$3000" → centavos; NaN si no es un monto válido (máx. 2 decimales)
// 20-oct-2026: la coma solo vale como separador de miles bien formado (grupos de 3). Con un teclado que usa coma
// decimal, "500,50" se leía como $50,050; ahora da NaN y el botón queda deshabilitado ("Escribe cuánto pagó").
export const aCent = (s) => {
  const t = String(s == null ? '' : s).replace(/[$\s]/g, '');
  if (!/^(\d+|\d{1,3}(,\d{3})+)(\.\d{0,2})?$/.test(t)) return NaN;
  return Math.round(parseFloat(t.replace(/,/g, '')) * 100);
};

// ── fechas (México, UTC-6 fijo como el resto de la app) ──
const hoyMx = () => new Date(Date.now() - 6 * 3600000).toISOString().slice(0, 10);
// Fecha y hora de México de un timestamp del servidor (viene en UTC): sin esto, lo validado a las 19:00
// salía con la fecha de mañana y el filtro del historial lo escondía. Una fecha sola (YYYY-MM-DD) se respeta.
const enMx = (v) => {
  const s = String(v || '');
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s + 'T00:00:00';
  const t = Date.parse(s);
  return isFinite(t) ? new Date(t - 6 * 3600000).toISOString() : '';
};
const fechaMx = (v) => enMx(v).slice(0, 10);
const horaMx = (v) => enMx(v).slice(11, 16);
const restaDias = (ymd, n) => new Date(Date.parse(ymd + 'T12:00:00Z') - n * 86400000).toISOString().slice(0, 10);
const horasDesde = (iso) => { const t = Date.parse(iso); return isFinite(t) ? Math.max(0, Math.floor((Date.now() - t) / 3600000)) : null; };
const haceTxt = (iso) => {
  const h = horasDesde(iso);
  if (h == null) return '';
  if (h < 1) return 'hace menos de 1 h';
  return h < 48 ? 'hace ' + h + ' h' : 'hace ' + Math.floor(h / 24) + ' días';
};
const diasVencido = (ymd) => {
  if (!ymd) return 0;
  const d = Math.round((Date.parse(hoyMx() + 'T12:00:00Z') - Date.parse(String(ymd).slice(0, 10) + 'T12:00:00Z')) / 86400000);
  return d > 0 ? d : 0;
};

// ── textos ──
const codigo = (pref, c) => {
  if (c == null || c === '') return pref + '-…';
  const s = String(c);
  return /^[A-Z]{2}-/.test(s) ? s : pref + '-' + s.padStart(6, '0');
};
const codAB = (c) => codigo('AB', c);
const codAN = (c) => codigo('AN', c);
const FORMAS_REP = [['transferencia', 'Transferencia o depósito'], ['mercado_pago', 'Mercado Pago'], ['tarjeta', 'Tarjeta'], ['efectivo', 'Efectivo en caja'], ['no_se', 'No sé']];
const FORMAS_VAL = [['transferencia', 'Transferencia'], ['mercado_pago', 'Mercado Pago'], ['tarjeta', 'Tarjeta'], ['efectivo', 'Efectivo']];
const NOM_FORMA = { transferencia: 'Transferencia', mercado_pago: 'Mercado Pago', tarjeta: 'Tarjeta', efectivo: 'Efectivo', no_se: 'No sabe la forma' };
const nomForma = (v) => NOM_FORMA[v] || v || '—';
const NOM_CANAL = { pwa: 'PWA', isabela: 'Isabela', takeover: 'WhatsApp (vendedora)', arranque: 'arranque' };
const MOTIVO_RESOLVER = {
  folio_cerrado: 'El apartado ya estaba cerrado cuando se validó.',
  en_entrega: 'El apartado se estaba cobrando en caja cuando se validó.',
  ticket_vivo: 'El apartado ya tenía ticket en caja cuando se validó.',
  cobrado_completo_en_entrega: 'Al entregar se cobró completo sin esperar a María.',
};
// 24-oct-2026: el pago del ENVÍO directo a la paquetería (CLABE de Skydropx) no es anticipo: se cierra sin caja con
// este motivo (el servidor lo anota en el envío del pedido del folio).
const MOTIVO_PAQ = 'pago_envio_paqueteria';
const TXT_PAQ = 'Pago a paquetería (envío)';
const MOTIVOS_RECHAZO = ['No llegó al banco', 'No es un comprobante', 'Duplicado', TXT_PAQ, 'Otro'];
const motivoTxt = (m) => (m === MOTIVO_PAQ ? 'PAGO A PAQUETERÍA (Skydropx) · no es anticipo' : (m || ''));
const errTxt = (e) => (e && e.data && e.data.error) || (e && e.message) || 'error';
const errCod = (e) => (e && e.data && e.data.codigo) || '';
const lista = (v) => (Array.isArray(v) ? v : []);
const cliNom = (it) => (it && (it.cliente || it.client_name || it.partner_name || (it.resumen && (it.resumen.cliente || it.resumen.client_name)))) || (it && it.partner_id ? 'Cliente #' + it.partner_id : 'Cliente');
const nombreReal = (it) => (it && (it.cliente || it.client_name || it.partner_name)) || '';   // sin "Cliente #id" de relleno
const urlArchivo = (id, tipo) => BASE + '/archivo/' + encodeURIComponent(id) + '?tipo=' + tipo;
// Miniatura de un pago: la foto chica; si es PDF, la vista previa de la página 1 (la hace el bot) o nada.
const miniDe = (p) => {
  if (!p || !p.archivo_id) return null;
  const ar = p.archivo || {};
  if (/pdf/i.test(ar.mime || '')) return ar.tiene_preview === false ? null : urlArchivo(p.archivo_id, 'preview');
  return urlArchivo(p.archivo_id, 'mini');
};
const metodoDe = (x) => x.metodo || (Array.isArray(x.payment_method_id) ? x.payment_method_id[1] : x.payment_method) || 'pago';
const normFolio = (s) => { const t = String(s || '').trim().toUpperCase(); return /^\d+$/.test(t) ? 'WA-' + t : t; };

// ── Lectura del comprobante (22-oct-2026) ──
// El bot LEE cada comprobante con IA (importe, folio de banco, fecha y hora, banco, cuenta destino) y lo manda en
// `pago.lectura`; `pago.duplicado` dice si el mismo folio de banco ya está en otro pago (real) o si hay otro con el
// mismo importe y hora sin folio (posible). Aquí solo se MUESTRA y se PRELLENA: María confirma contra el banco.
// Sin lectura (bandera apagada, IA sin crédito, ilegible) la tarjeta y el formulario quedan como antes.
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const fechaCorta = (v) => { const s = enMx(v); return s ? Number(s.slice(8, 10)) + '-' + MESES[Number(s.slice(5, 7)) - 1] : ''; };
const lectOk = (p) => (p && p.lectura && p.lectura.estado === 'leido') ? p.lectura : null;
const refBanco = (l) => (!l ? '' : l.folio ? 'folio ' + l.folio : l.clave_rastreo ? 'rastreo ' + l.clave_rastreo : l.autorizacion ? 'aut. ' + l.autorizacion : '');
// "$3,875 · BBVA folio 0020097225 · 21-oct 16:30" (en partes: el importe y la fecha no se parten al cambiar de renglón)
const partesLectura = (l) => {
  if (!l) return [];
  const banco = [l.banco_emisor, refBanco(l)].filter(Boolean).join(' ');
  const cuando = l.fecha_hora ? fechaCorta(l.fecha_hora) + (l.con_hora ? ' ' + horaMx(l.fecha_hora) : '') : '';
  return [[l.importe != null ? $p(l.importe) : 'sin importe', true], [banco, false], [cuando, true]].filter(x => x[0]);
};
const LineaLectura = ({ l }) => partesLectura(l).map(([t, junto], i) => (
  <React.Fragment key={i}>{i ? ' · ' : ''}<span style={junto ? { whiteSpace: 'nowrap' } : undefined}>{t}</span></React.Fragment>));
const TIPO_LECT = { transferencia: 'Transferencia', deposito: 'Depósito', oxxo: 'OXXO', tarjeta: 'Tarjeta', mercado_pago: 'Mercado Pago', otro: 'Otro' };
const CUENTA_TXT = {
  oficial: ['ok', 'es la cuenta oficial'],
  otra: ['bad', 'NO es la cuenta oficial'],
  terminacion_oficial: ['', 'viene tapada; termina igual que la oficial (confírmalo en el banco)'],
  terminacion_otra: ['warn', 'viene tapada y NO termina como la oficial'],
  sin_dato: ['', 'no se ve en el comprobante'],
  paqueteria: ['ap', 'es la cuenta de la PAQUETERÍA (Skydropx): pago del envío, no es anticipo'],
};
const esPagoPaq = (p) => { const l = lectOk(p); return !!(l && l.cuenta_oficial === 'paqueteria'); };
const ESTADO_OP_MAL = { programada: 'PROGRAMADA', en_proceso: 'EN PROCESO', rechazada: 'RECHAZADA' };
const conDup = (d) => lista(d && d.con).map(o => codAB(o.codigo) + (o.folio ? ' (' + o.folio + (o.misma_clienta === false ? ', otra clienta' : '') + ')' : ''));
// Revisión 22-oct: por qué es "posible" (la misma "llave" de banco con OTRO importe no bloquea, pero se ve).
const porQueDup = (d) => (d && d.motivo === 'mismo_folio_otro_importe' ? ' (mismo folio, otro importe)' : '');
// Revisión 22-oct (alucinaciones): solo se PRELLENA lo leído con confianza alta/media, legible y operación no
// marcada como programada/en proceso/rechazada. Lo demás se muestra, pero María lo escribe ella.
// 24-oct: un pago a la paquetería tampoco se prellena (no es anticipo).
const lectPrellena = (l) => (l && l.confianza !== 'baja' && l.legible !== false && !ESTADO_OP_MAL[l.estado_operacion] && l.cuenta_oficial !== 'paqueteria' ? l : null);
// Chips de la lectura para la tarjeta y el detalle: [clase, texto]. Rojos: duplicado real, cuenta no oficial y
// operación no exitosa; ámbar: posible duplicado, terminación distinta, leído ≠ reportado/validado, no parece
// comprobante; grises: leyendo / sin lectura. conPosible: el modal (la tarjeta ya pinta "Posible duplicado" aparte).
function chipsLectura(p, conPosible) {
  const out = [];
  const l = p && p.lectura;
  const d = p && p.duplicado;
  if (d && d.nivel === 'real') out.push(['bad', 'DUPLICADO de ' + conDup(d).join(', ')]);
  if (d && d.nivel === 'posible' && (conPosible || !p.posible_duplicado)) out.push(['warn', 'Posible duplicado de ' + conDup(d).join(', ') + porQueDup(d)]);
  if (!l) return out;
  if (l.estado === 'pendiente' || l.estado === 'leyendo') out.push(['', 'Leyendo comprobante…']);
  else if (l.estado === 'fallida') out.push(['', 'Sin lectura']);
  else if (l.estado === 'no_comprobante') out.push(['warn', 'No parece comprobante']);
  else if (l.estado === 'leido') {
    if (l.cuenta_oficial === 'paqueteria') out.push(['ap', 'Pago a paquetería (Skydropx) · no es anticipo']);
    if (l.cuenta_oficial === 'otra') out.push(['bad', 'Cuenta NO oficial' + (l.cuenta_destino_ult4 ? ' …' + l.cuenta_destino_ult4 : '')]);
    if (l.cuenta_oficial === 'terminacion_otra') out.push(['warn', 'Termina en …' + (l.cuenta_destino_ult4 || '?') + ', no es la oficial']);
    if (ESTADO_OP_MAL[l.estado_operacion]) out.push(['bad', 'El comprobante dice ' + ESTADO_OP_MAL[l.estado_operacion]]);
    if (l.importe != null && p.monto_reportado != null && cents(l.importe) !== cents(p.monto_reportado)) out.push(['warn', 'Leído ' + $p(l.importe) + ' ≠ reportado ' + $p(p.monto_reportado)]);
    // carrera lectura ↔ validación: María validó antes de que terminara la lectura y no cuadra (±$1)
    if (l.importe != null && p.estado === 'validado' && p.monto_real != null && Math.abs(cents(l.importe) - cents(p.monto_real)) > 100) out.push(['warn', 'Leído ' + $p(l.importe) + ' ≠ validado ' + $p(p.monto_real)]);
    if (l.confianza === 'baja') out.push(['', 'Lectura dudosa']);
  }
  return out;
}
// Fecha leída que sirve para validar: de los últimos 60 días y no futura (si no, el formulario arranca en hoy).
const fechaLeida = (l) => {
  const f = l && l.fecha_hora ? fechaMx(l.fecha_hora) : '';
  return f && f <= hoyMx() && f >= restaDias(hoyMx(), 60) ? f : '';
};

// ── contador (también guarda si el efectivo nace al reportar: modo B, decisión final del dueño) ──
// /contador es lo único que contesta con el control apagado (ceros + activo:false). Se guarda aquí para
// que, apagado, ninguna pieza pinte nada ni pida nada de más: la pantalla queda exactamente como hoy.
let _cont = null;
export async function cargarContadorPagosAp(api) {
  const r = await api(BASE + '/contador');
  _cont = r || null;
  return r;
}
const efectivoAlReportar = () => !(_cont && _cont.efectivo_al_reportar === false);
const apagado = () => !!(_cont && _cont.activo === false);

// ── CSS .pa- (tokens del Design System 2.0) ──
const CSS_PA = `@import url('https://fonts.googleapis.com/css2?family=Jost:wght@400;500;600&display=swap');
.pa{--ink:#0A0A0A;--mut:#6B6B6B;--line:#E5E5E5;--soft:#F5F5F5;--ok:#17663A;--okb:#E6F3EA;--warn:#B45309;--warnb:#FFF0DB;--err:#B3261E;--errb:#FCEBEA;--ap:#6D28D9;--apb:#F1EAFE;font-family:'Jost','Helvetica Neue',Arial,sans-serif;color:var(--ink);-webkit-font-smoothing:antialiased;text-align:left;line-height:1.4;font-size:14px}
.pa *{box-sizing:border-box}
.pa button{font-family:inherit;cursor:pointer;-webkit-appearance:none;appearance:none;border-radius:0}
.pa input,.pa select,.pa textarea{font-family:inherit;border-radius:0}
.pa button:focus-visible,.pa input:focus-visible,.pa select:focus-visible,.pa textarea:focus-visible{outline:2px solid var(--ink);outline-offset:2px}
.pa-res{margin:6px 0 8px;font-size:13px}
.pa-res .l{font-weight:500;font-variant-numeric:tabular-nums}
.pa-gris{color:var(--mut);font-size:12px}
.pa-chips{display:flex;flex-wrap:wrap;gap:6px;margin-top:6px}
.pa-chips span{font-size:11px;background:var(--soft);padding:3px 7px;line-height:1.3}
.pa-chips .warn{background:var(--warnb);color:var(--warn)}
.pa-chips .bad{background:var(--errb);color:var(--err);font-weight:600}
.pa-chips .ok{background:var(--okb);color:var(--ok)}
.pa-chips .ap{background:var(--apb);color:var(--ap)}
.pa-btn{display:inline-flex;align-items:center;justify-content:center;gap:6px;min-height:48px;padding:0 14px;border:1px solid var(--ink);background:var(--ink);color:#FFFFFF;font-size:12px;letter-spacing:1.4px;font-weight:500;text-transform:uppercase;text-align:center;line-height:1.2}
.pa-btn.full{display:flex;width:100%}
.pa-btn.ghost{background:#FFFFFF;color:var(--ink)}
.pa-btn.ok{background:var(--ok);border-color:var(--ok);color:#FFFFFF}
.pa-btn.bad{background:#FFFFFF;color:var(--err);border-color:var(--err)}
.pa-btn.sm{min-height:40px;padding:0 10px;font-size:11px;letter-spacing:1px}
.pa-btn:disabled{background:var(--line);border-color:var(--line);color:var(--mut);cursor:default}
.pa-banner{display:flex;align-items:center;gap:10px;border:2px solid var(--err);background:var(--errb);color:var(--err);padding:10px 12px;margin:0 0 12px;font-size:13px;font-weight:500}
.pa-banner span{flex:1;min-width:0}
.pa-banner .pa-btn{background:var(--err);border-color:var(--err);flex-shrink:0}
.pa-reenv{border:2px solid var(--err);background:var(--errb);padding:10px 12px;margin:0 0 12px;font-size:13px;line-height:1.45;color:#7A1C16}
.pa-reenv b{color:var(--err)}
.pa-reenv .pa-btn{margin-top:8px;background:var(--err);border-color:var(--err)}
.pa-sheet{position:fixed;top:0;bottom:0;left:0;right:0;margin:0 auto;width:100%;max-width:560px;background:#FFFFFF;z-index:10020;display:flex;flex-direction:column}
.pa-top{display:flex;align-items:center;gap:6px;min-height:56px;padding:0 8px 0 4px;border-bottom:1px solid var(--line);flex-shrink:0}
.pa-x{width:44px;height:44px;border:none;background:none;font-size:28px;line-height:1;color:var(--ink);flex-shrink:0;padding:0}
.pa-t{flex:1;min-width:0;font-size:16px;font-weight:500;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.pa-t small{display:block;font-size:12px;color:var(--mut);font-weight:400;overflow:hidden;text-overflow:ellipsis}
.pa-tabs{display:flex;gap:16px;overflow-x:auto;border-bottom:1px solid var(--line);padding:0 16px;flex-shrink:0;-webkit-overflow-scrolling:touch;scrollbar-width:none}
.pa-tabs::-webkit-scrollbar{display:none}
.pa-tabs button{flex-shrink:0;background:none;border:none;border-bottom:2px solid transparent;height:46px;padding:0;font-size:12px;letter-spacing:1.2px;text-transform:uppercase;font-weight:500;color:var(--mut);white-space:nowrap}
.pa-tabs button.on{color:var(--ink);border-bottom-color:var(--ink)}
.pa-tabs b{display:inline-block;min-width:18px;margin-left:5px;padding:0 5px;background:var(--err);color:#FFFFFF;font-size:11px;letter-spacing:0;font-weight:600;text-align:center}
.pa-body{flex:1;overflow-y:auto;-webkit-overflow-scrolling:touch;padding:14px 16px 24px}
.pa-foot{flex-shrink:0;border-top:1px solid var(--line);padding:10px 16px calc(10px + env(safe-area-inset-bottom));display:flex;gap:8px;background:#FFFFFF}
.pa-foot .pa-btn{flex:1;min-width:0}
.pa-modal{position:fixed;top:0;right:0;bottom:0;left:0;z-index:10030;background:rgba(10,10,10,.45);display:flex;align-items:flex-end;justify-content:center}
.pa-card{background:#FFFFFF;width:100%;max-width:520px;max-height:94vh;display:flex;flex-direction:column}
@media (min-width:600px){.pa-modal{align-items:center;padding:16px}}
.pa-item{border:1px solid var(--line);padding:12px;margin-bottom:10px;background:#FFFFFF}
.pa-item.din{border:2px solid var(--err)}
.pa-item-h{display:flex;gap:10px;align-items:flex-start}
.pa-item-h .tx{flex:1;min-width:0}
.pa-item-h b{font-size:15px;font-weight:500;overflow-wrap:anywhere}
.pa-item-h .m{font-size:15px;font-weight:500;white-space:nowrap;font-variant-numeric:tabular-nums}
.pa-sub{font-size:12px;color:var(--mut);margin-top:3px;line-height:1.45;overflow-wrap:anywhere}
.pa-acts{display:flex;flex-wrap:wrap;gap:6px;margin-top:10px}
.pa-acts .pa-btn{flex:1 1 auto}
.pa-vacio{padding:28px 12px;text-align:center;color:var(--mut);font-size:14px}
.pa-mini{width:56px;height:56px;flex-shrink:0;background:var(--soft);display:flex;align-items:center;justify-content:center;overflow:hidden;font-size:10px;color:var(--mut);border:none;padding:0;text-align:center}
.pa-mini img{width:100%;height:100%;object-fit:cover;display:block}
.pa-lbl{display:block;font-size:11px;letter-spacing:1.4px;text-transform:uppercase;color:var(--mut);font-weight:500;margin:14px 0 6px}
.pa-in{width:100%;height:48px;border:1px solid var(--ink);padding:0 12px;font-size:16px;background:#FFFFFF;color:var(--ink);outline:none}
textarea.pa-in{height:auto;min-height:64px;padding:10px 12px;resize:vertical}
select.pa-in{padding:0 8px}
.pa-seg{display:grid;grid-template-columns:1fr 1fr;gap:6px}
.pa-seg button{min-height:44px;border:1px solid var(--line);background:#FFFFFF;color:var(--ink);font-size:13px;padding:4px 8px;line-height:1.2}
.pa-seg button.on{border:2px solid var(--ink);font-weight:600}
.pa-ck{display:flex;gap:10px;align-items:flex-start;margin:12px 0 0;font-size:14px;line-height:1.35;cursor:pointer}
.pa-ck input{width:22px;height:22px;flex-shrink:0;margin:0;accent-color:#0A0A0A}
.pa-note{background:var(--soft);padding:10px 12px;font-size:13px;line-height:1.45;margin:10px 0 0}
.pa-note.warn{background:var(--warnb);color:#7A3E06}
.pa-note.bad{background:var(--errb);color:var(--err)}
.pa-note.ok{background:var(--okb);color:var(--ok)}
.pa-file{display:flex;align-items:center;gap:10px;border:1px dashed var(--ink);padding:8px 10px;min-height:56px;cursor:pointer;font-size:14px}
.pa-file input{display:none}
.pa-rep{border:1px solid var(--line);margin-top:6px}
.pa-rep-r{display:flex;align-items:center;gap:8px;padding:8px 10px;border-bottom:1px solid var(--line);flex-wrap:wrap}
.pa-rep-r:last-child{border-bottom:none}
.pa-rep-r .tx{flex:1;min-width:0;font-size:13px}
.pa-rep-r .tx small{display:block;color:var(--mut);font-size:11.5px}
.pa-rep-r input{width:112px;height:42px;border:1px solid var(--ink);padding:0 8px;font-size:16px;text-align:right}
.pa-rep-r .av{flex-basis:100%;font-size:11.5px;color:var(--warn)}
.pa-cuadre{margin-top:8px;padding:8px 10px;font-size:13px;font-weight:600}
.pa-cuadre.ok{background:var(--okb);color:var(--ok)}
.pa-cuadre.bad{background:var(--errb);color:var(--err)}
.pa-comp{background:var(--soft);display:flex;align-items:center;justify-content:center;min-height:140px;max-height:46vh;overflow:hidden;border:none;padding:0;width:100%}
.pa-comp img{max-width:100%;max-height:46vh;display:block}
.pa-visor{position:fixed;top:0;right:0;bottom:0;left:0;z-index:10040;background:#0A0A0A;display:flex;flex-direction:column;padding-top:env(safe-area-inset-top);padding-bottom:env(safe-area-inset-bottom)}
.pa-visor .pa-top{border-bottom-color:#262626;color:#FFFFFF}
.pa-visor .pa-x{color:#FFFFFF}
.pa-visor .pa-btn{background:#FFFFFF;color:var(--ink);border-color:#FFFFFF}
.pa-visor-b{flex:1;overflow:auto;-webkit-overflow-scrolling:touch;display:flex;align-items:flex-start;justify-content:center}
.pa-visor-b img{max-width:100%;display:block}
.pa-visor-b img.zoom{max-width:none;width:220%}
.pa-visor-b object{width:100%;height:100%;border:none;background:#FFFFFF}
.pa-fila{display:flex;gap:8px;align-items:center}
.pa-fila>*{flex:1;min-width:0}
`;
let _css = false;
function ponerCSS() {
  if (_css || typeof document === 'undefined') return;
  _css = true;
  if (document.getElementById('pa-css')) return;
  const s = document.createElement('style');
  s.id = 'pa-css';
  s.textContent = CSS_PA;
  document.head.appendChild(s);
}

// ¿El folio tiene algo de dinero que mostrar? (sin dinero la tarjeta queda como hoy)
const hayPagos = (r) => !!r && (!!r.tiene_dinero || r.por_validar_n > 0 || r.por_resolver_n > 0 || !!r.entrega_activa ||
  [r.registrado, r.disponible, r.en_cola, r.efectivo_sin_cobrar, r.por_validar_monto, r.por_devolver, r.saldo_a_favor_folio, r.saldo_cliente, r.aplicado].some(v => cents(v) > 0));

// ════════════════════════════════════════════════════════════════════════
// Resumen en la tarjeta del apartado (los dos paneles)
// ════════════════════════════════════════════════════════════════════════
export function ResumenPagosAp({ a, onRegistrar, puedeValidar }) {
  if (!a) return null;
  const r = a.pagos;
  // Envío por paquetería (22-oct-2026): lo cobrado va en el ticket como línea "Envío" y se suma al total.
  const env = a.envio || null;
  const E = env && !env.falta_cobro && !env.en_lineas ? cents(env.cobrado) : 0;
  const chipEnv = !env ? null : (env.falta_cobro
    ? <span key="env" className="warn">Se envía por paquetería: falta capturar lo cobrado del envío (Envíos por paquetería)</span>
    : <span key="env" className="ap">{env.pagado_directo ? 'Envío pagado directo a la paquetería (fuera de la caja)'
      : E > 0 ? (env.nombre || 'Envío') + ' ' + $c(E) + ' va en el ticket' : (env.en_lineas ? 'El envío ya va en las prendas' : 'Envío sin cobro')}</span>);
  if (r == null) {
    if (apagado() && !chipEnv) return null;   // control apagado: la tarjeta queda como hoy aunque el bot no conteste
    ponerCSS();
    return <div className="pa pa-res" data-pa="resumen">{!apagado() && <span className="pa-gris">Pagos: sin conexión</span>}{chipEnv && <div className="pa-chips">{chipEnv}</div>}</div>;
  }
  if (!hayPagos(r)) {
    if (!chipEnv) return null;
    ponerCSS();
    return <div className="pa pa-res" data-pa="resumen"><div className="pa-chips">{chipEnv}</div></div>;
  }
  ponerCSS();
  const T = cents(r.importe != null ? r.importe : a.importe) + E;   // con envío: prendas + envío, como el ticket
  // pagado = disponible del folio + en cola + efectivo sin cobrar (lo por validar NO cuenta)
  const P = r.pagado != null ? cents(r.pagado) : cents(r.disponible) + cents(r.en_cola) + cents(r.efectivo_sin_cobrar);
  const R = (r.resta != null && E === 0) ? cents(r.resta) : Math.max(0, T - P);
  const S = cents(r.saldo_cliente) + cents(r.saldo_a_favor_folio);
  const chips = [];
  if (chipEnv) chips.push(chipEnv);
  if (r.por_validar_n > 0) chips.push(<span key="pv" className="warn">{r.por_validar_n} por validar ({$p(r.por_validar_monto)})</span>);
  if (cents(r.efectivo_sin_cobrar) > 0) chips.push(<span key="ef" className="warn">Efectivo {$p(r.efectivo_sin_cobrar)} sin cobrar en caja</span>);
  if (cents(r.en_cola) > 0) chips.push(<span key="co">En cola de caja {$p(r.en_cola)}</span>);
  if (r.por_resolver_n > 0) chips.push(<span key="pr" className="warn">{r.por_resolver_n} por resolver</span>);
  if (cents(r.por_devolver) > 0) chips.push(<span key="pd" className="warn">Por devolver {$p(r.por_devolver)}</span>);
  if (r.vencido) chips.push(<span key="ve" className="bad">Vencido con dinero</span>);
  if (S > 0) chips.push(<span key="sa" className="ok">Saldo a favor de la clienta {$c(S)}</span>);
  if (r.entrega_activa) chips.push(<span key="en" className="ap">Ticket en caja{r.entrega_activa.ticket ? ' ' + r.entrega_activa.ticket : ''}</span>);
  return (
    <div className="pa pa-res" data-pa="resumen">
      <div className="l">Pagado {$c(P)} de {$c(T)} · resta {$c(R)}</div>
      {chips.length > 0 && <div className="pa-chips">{chips}</div>}
      {puedeValidar && r.bloquea_borrar && Array.isArray(r.motivos_bloqueo) && r.motivos_bloqueo.length > 0 && (
        <div className="pa-gris" style={{ marginTop: 4 }}>No se puede borrar ({r.motivos_bloqueo.join(', ').replace(/_/g, ' ')}): decide saldo a favor o devolución.</div>
      )}
      {onRegistrar && <button type="button" className="pa-btn sm ghost" style={{ marginTop: 6 }} onClick={() => onRegistrar(a)}>Registrar pago</button>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Aviso arriba de Pedidos apartados (solo quien valida)
// ════════════════════════════════════════════════════════════════════════
export function BannerPagosAp({ cont, onAbrir }) {
  if (!cont || !cont.puede_validar || cont.activo === false) return null;
  const pv = cont.por_validar_n || 0, pr = (cont.por_resolver_n || 0) + (cont.por_decidir_n || 0), al = cont.alertas_dinero_n || 0;
  const ve = cont.vencidos_n || 0, caja = (cont.en_cola_n || 0) + (cont.efectivo_sin_cobrar_n || 0) + (cont.por_devolver_n || 0);
  ponerCSS();
  // 20-oct-2026 (revisión "tienda"): este banner es la ÚNICA puerta a la bandeja. Sin pendientes no se pintaba y
  // María no llegaba a Saldos a favor, Historial ("Deshacer validación") ni a "Registrar pago de otro folio".
  if (!(pv || pr || al || ve || caja)) return (
    <div className="pa" role="status" data-pa="banner-vacio" style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 12px', fontSize: 13 }}>
      <span style={{ flex: 1 }}>💵 Pagos de apartados: nada pendiente</span>
      <button type="button" className="pa-btn sm ghost" onClick={onAbrir}>Abrir bandeja</button>
    </div>
  );
  const partes = [];
  if (pv) partes.push(pv + ' pago' + (pv === 1 ? '' : 's') + ' por validar');
  if (pr) partes.push(pr + ' por resolver');
  if (al) partes.push(al + ' alerta' + (al === 1 ? '' : 's'));
  if (ve) partes.push(ve + ' vencido' + (ve === 1 ? '' : 's') + ' con dinero');
  if (caja) partes.push(caja + ' en caja');
  return (
    <div className="pa pa-banner" role="status" data-pa="banner">
      <span>💵 {partes.join(' · ')}</span>
      <button type="button" className="pa-btn sm" onClick={onAbrir}>Abrir</button>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Reenviar a caja (la cajera canceló el ticket de entrega y el anticipo sigue registrado)
// ════════════════════════════════════════════════════════════════════════
// Usa las MISMAS líneas y la MISMA vendedora del ticket original (lo hace el servidor, §5.6).
// Nombre del ticket en la respuesta (la de /send trae odooResult.order.name; la de reenviar, ticket).
const ticketDe = (r) => (r && (r.ticket || r.pos_ref || (r.odooResult && r.odooResult.order && r.odooResult.order.name) || (r.order && r.order.name))) || '';
// Aviso rojo cuando la relectura del ticket no cuadró con el anticipo (§5.1 E8): NO se cobra así.
export const alertaTicket = (r) => {
  const an = (r && r.anticipo) || {};
  return an.alerta || an.aviso || (r && (r.alerta_anticipo || r.aviso_anticipo)) || '';
};
// Prendas del ticket original (las guardó el libro): "BLUSA X M Azul · 4 pz · $500".
const lineasDe = (en) => {
  let ls = en && en.lineas;
  if (typeof ls === 'string') { try { ls = JSON.parse(ls); } catch (e) { ls = []; } }
  return lista(ls);
};
const piezasDe = (l) => Number(l.total_pieces || l.qty || l.pzs || 1) || 1;
const precioDe = (l) => cents(l.unit_price != null ? l.unit_price : l.price);
const nombreLinea = (l) => (l && l.es_envio)   // 22-oct-2026: la línea del envío por paquetería no es prenda
  ? (l.product_name || 'Envío') + ' · envío · ' + $c(precioDe(l))
  : [l.product_name || l.name || 'Prenda', l.talla, l.color].filter(Boolean).join(' ') + ' · ' + piezasDe(l) + ' pz · ' + $c(piezasDe(l) * precioDe(l));
// 20-oct-2026 (revisión "tienda"): la capacitación pide reenviar "con las prendas que sí se lleva" (si deja prendas
// y el total queda negativo, la cajera cancela). Con más de una prenda se pregunta cuáles; el servidor ya acepta
// line_idx y topa X a las prendas elegidas (lo que sobre del anticipo queda a favor de la clienta).
async function reenviarEntrega(api, en, showToast, opciones = {}, intento = 0) {
  const ls = lineasDe(en);
  // line_idx ya elegido en un intento anterior (X_CAMBIO / SALDO_CLIENTE): [] = todas; no se vuelve a preguntar
  let sel = Array.isArray(opciones.line_idx) ? (opciones.line_idx.length ? opciones.line_idx : null) : undefined;
  if (sel === undefined && ls.length > 1) {
    const r = window.prompt('¿Qué prendas se lleva la clienta? Escribe los números separados por coma (vacío = todas):\n\n' +
      ls.map((l, i) => (i + 1) + '. ' + nombreLinea(l)).join('\n'), '');
    if (r === null) return false;
    const nums = String(r).split(/[^0-9]+/).filter(Boolean).map(n => parseInt(n, 10) - 1);
    if (nums.length) {
      if (nums.some(n => !(n >= 0 && n < ls.length))) { alert('Ese número de prenda no existe. No se reenvió nada.'); return false; }
      sel = Array.from(new Set(nums)).sort((a, b) => a - b);
      if (sel.length === ls.length) sel = null;   // todas: como antes
    }
  }
  if (sel === undefined) sel = null;
  const desc = Number(en.descuento) || 0;
  // La línea del envío por paquetería (es_envio, 22-oct-2026) no lleva el descuento de las prendas.
  const T = sel ? Math.round(sel.filter(i => !ls[i].es_envio).reduce((s, i) => s + piezasDe(ls[i]) * precioDe(ls[i]), 0) * (desc > 0 ? 1 - desc / 100 : 1))
    + sel.filter(i => ls[i].es_envio).reduce((s, i) => s + precioDe(ls[i]), 0) : cents(en.total_prendas);
  const X0 = opciones.x != null ? cents(opciones.x) : cents(en.anticipo);
  const X = Math.min(X0, T);
  const txt = (opciones.aviso ? opciones.aviso + '\n\n' : '') + 'Reenviar a caja el ticket de ' + (en.folio || 'la clienta') + (sel ? ' solo con ' + sel.length + ' de ' + ls.length + ' prendas' : '') +
    '.\n\nTotal ' + $c(T) + ' · anticipo −' + $c(X) + ' · en caja se cobra ' + $c(Math.max(0, T - X)) +
    (X < X0 ? '\nEl resto del anticipo (' + $c(X0 - X) + ') queda a favor de la clienta.' : '') +
    '\n\nLas piezas ya regresaron al piso: confirma que sigan ahí antes de cobrar.';
  if (!window.confirm(txt)) return false;
  const body = { x_esperado: pesos(X) };
  if (sel) body.line_idx = sel;
  if (typeof opciones.aplicar_saldo === 'boolean') body.aplicar_saldo = opciones.aplicar_saldo;
  try {
    const r = await api(BASE + '/entregas/' + encodeURIComponent(en.id) + '/reenviar', { method: 'POST', body });
    const an = (r && r.anticipo) || {};
    const x2 = an.X != null ? cents(an.X) : X, t2 = an.T != null ? cents(an.T) : T;
    showToast && showToast('✅ Ticket ' + ticketDe(r) + ' en caja · se cobra ' + $c(Math.max(0, t2 - x2)));
    const roja = alertaTicket(r);
    if (roja) alert(roja);   // el ticket salió sin el anticipo correcto: NO cobrarlo
    return true;
  } catch (e) {
    const d = (e && e.data) || {};
    if (intento < 3 && d.codigo === 'X_CAMBIO') {
      return reenviarEntrega(api, en, showToast, { ...opciones, line_idx: sel || [], x: d.X, aviso: 'El anticipo cambió: ahora es ' + $p(d.X) + '.' }, intento + 1);
    }
    if (intento < 3 && d.codigo === 'SALDO_CLIENTE' && typeof opciones.aplicar_saldo !== 'boolean') {
      const sal = window.confirm(textoSaldo(d));
      return reenviarEntrega(api, en, showToast, { ...opciones, line_idx: sel || [], aplicar_saldo: sal }, intento + 1);
    }
    alert(errTxt(e));
    return false;
  }
}

export function ListaReenvios({ api, onReenviar, showToast }) {
  const [entregas, setEntregas] = useState([]);
  const [ocupado, setOcupado] = useState('');
  const cargar = useCallback(async () => {
    // Con el control apagado (o sin conexión) no se pinta nada, como hoy; apagado ni siquiera se pregunta.
    if (apagado()) { setEntregas([]); return; }
    try { const r = await api(BASE + '/reenvios?mios=1'); setEntregas(lista(r && r.entregas)); } catch (e) { setEntregas([]); }
  }, [api]);
  useEffect(() => { cargar(); }, [cargar]);
  if (!entregas.length) return null;
  ponerCSS();
  const reenviar = async (en) => {
    if (ocupado) return;
    setOcupado(en.id);
    try { const ok = await reenviarEntrega(api, en, showToast); if (ok) { cargar(); if (onReenviar) onReenviar(en); } }
    finally { setOcupado(''); }
  };
  return (
    <div className="pa" data-pa="reenvios">
      {entregas.map(en => (
        <div key={en.id} className="pa-reenv" role="alert">
          <b>La cajera canceló el ticket {en.ticket_name || ''} de {en.folio || 'la clienta'}.</b> El anticipo de {$p(en.anticipo)} sigue a favor de la clienta. Cuando la clienta esté en caja:
          <button type="button" className="pa-btn full" disabled={!!ocupado} onClick={() => reenviar(en)}>{ocupado === en.id ? 'Enviando…' : 'Reenviar a caja'}</button>
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Entrega: previa y preguntas antes de mandar a caja (§5.5)
// ════════════════════════════════════════════════════════════════════════
// La previa SOLO LEE (ni Odoo ni el libro cambian). Lo que el dueño pidió decidir en pantalla (pago por
// validar, efectivo sin cobrar, saldo a favor) se pregunta UNA vez y la respuesta viaja en el cuerpo; el
// servidor vuelve a revisar todo y falla cerrado. Red de seguridad: siempre va x_esperado con el anticipo
// que la vendedora VIO; si el servidor calcula otro, contesta X_CAMBIO y aquí se vuelve a confirmar con el
// real. Así nunca sale un ticket con un anticipo que nadie confirmó.
// Los campos de la previa pueden venir como lista, como conteo o como {n, monto, pagos}: se aceptan todos.
const idsAsig = (lst) => lista(lst).map(a => a.asignacion_id || a.id).filter(Boolean);
const COD_DECIDIR = ['POR_VALIDAR', 'EFECTIVO_SIN_COBRAR', 'SALDO_CLIENTE'];
function infoPorValidar(v) {   // → { n, c (centavos), pagos }
  if (Array.isArray(v)) return { n: v.length, c: v.reduce((s, p) => s + cents(p.monto_reportado), 0), pagos: v };
  if (v && typeof v === 'object') {
    const pagos = lista(v.pagos);
    return { n: Number(v.n) || pagos.length, c: v.monto != null ? cents(v.monto) : pagos.reduce((s, p) => s + cents(p.monto_reportado), 0), pagos };
  }
  return { n: Number(v) || 0, c: 0, pagos: [] };
}
function infoEfectivo(v) {     // → { c, asigs }
  if (Array.isArray(v)) return { c: v.reduce((s, a) => s + cents(a.monto), 0), asigs: v };
  if (v && typeof v === 'object') { const asigs = lista(v.asignaciones); return { c: v.monto != null ? cents(v.monto) : asigs.reduce((s, a) => s + cents(a.monto), 0), asigs }; }
  return { c: cents(v), asigs: [] };
}
function infoSaldo(v) {        // → { c, detalle }
  if (v && typeof v === 'object') {
    const s = v.saldo && typeof v.saldo === 'object' ? v.saldo : v;
    return { c: cents(s.saldo != null ? s.saldo : s.monto), detalle: lista(s.detalle || v.detalle) };
  }
  return { c: cents(v), detalle: [] };
}
const textoPorValidar = (pv) => {
  const p = pv.pagos[0] || {};
  const quien = p.reportado_por ? ', lo reportó ' + p.reportado_por + (p.horas != null ? ' hace ' + Math.round(p.horas) + ' h' : '') : '';
  const monto = p.monto_reportado != null ? $p(p.monto_reportado) : (pv.c > 0 ? $c(pv.c) : 'monto sin dato');
  const cual = p.codigo != null ? ' (' + codAB(p.codigo) + quien + ')' : '';
  return 'Hay ' + (pv.n > 1 ? pv.n + ' pagos por validar; el primero es' : 'un pago por validar') + ' de ' + monto + cual + '. María aún no lo revisa.\n\n' +
    'Aceptar = COBRAR COMPLETO: ese pago queda para que María lo resuelva.\nCancelar = ESPERAR A MARÍA (no se envía).';
};
const textoEfectivo = (ef) => {
  const cods = ef.asigs.filter(a => a.codigo != null).map(a => codAN(a.codigo)).join(', ');
  return 'El anticipo en efectivo ' + (cods ? cods + ' ' : '') + '(' + $c(ef.c) + ') sigue sin cobrarse en caja.\n\n' +
    'Aceptar = LA CLIENTA LO PAGA AHORA JUNTO CON TODO (se cancela ese ticket ANTICIPO).\nCancelar = ESPERAR A QUE LA CAJERA LO COBRE (no se envía).';
};
function textoSaldo(d) {
  const s = infoSaldo(d);
  const folios = Array.from(new Set(s.detalle.map(x => x.folio).filter(Boolean))).join(', ');
  return 'La clienta tiene ' + $c(s.c) + ' a favor' + (folios ? ' (' + folios + ')' : '') + '. ¿Descontarlo en este ticket?\n\nAceptar = sí, descontarlo.\nCancelar = no, cobrar sin el saldo.';
}

// prepararEntregaAnticipo(api, a, origen, extra) → { ok, confirmado, body, previo, T, X }
//   a: el apartado de la lista ({id, folio, client_name}); origen: 'pwa' | 'wa' (extra.soid, extra.line_ids)
//   extra.conflicto: e.data de un 409 de /send (POR_VALIDAR, EFECTIVO_SIN_COBRAR, SALDO_CLIENTE, X_CAMBIO)
//   extra.previo:    lo que devolvió el intento anterior en `previo` (no se vuelve a preguntar lo ya decidido)
// body = undefined cuando el control está apagado o la previa no contesta y no hay nada decidido: la
// entrega va EXACTAMENTE como hoy y el servidor decide (falla cerrado si el folio tiene dinero).
export async function prepararEntregaAnticipo(api, a, origen, extra = {}) {
  const prev = (extra && extra.previo) || {};
  const ant = {};
  const dec = { efectivo_ahora: !!prev.efectivo_ahora };
  if (prev.ignorar_por_validar) ant.ignorar_por_validar = true;
  if (lista(prev.cancelar_efectivo).length) ant.cancelar_efectivo = prev.cancelar_efectivo.slice();
  if (typeof prev.aplicar_saldo === 'boolean') ant.aplicar_saldo = prev.aplicar_saldo;
  const sumaEfectivo = (ids) => { ant.cancelar_efectivo = Array.from(new Set([...(ant.cancelar_efectivo || []), ...ids])); };
  let avisoX = '', xServidor = null;
  const conf = extra && extra.conflicto;
  if (conf && conf.codigo === 'POR_VALIDAR') {
    if (!window.confirm(textoPorValidar(infoPorValidar(conf.pagos)))) return { ok: false };
    ant.ignorar_por_validar = true;
  } else if (conf && conf.codigo === 'EFECTIVO_SIN_COBRAR') {
    const ef = infoEfectivo(conf.asignaciones), ids = idsAsig(ef.asigs);
    if (!ids.length) { alert(conf.error || 'Hay un anticipo en efectivo sin cobrar en caja.'); return { ok: false }; }
    // si ya dijo "la clienta lo paga ahora" con la previa, aquí solo faltaban los números de asignación
    if (!dec.efectivo_ahora && !window.confirm(textoEfectivo(ef))) return { ok: false };
    dec.efectivo_ahora = true; sumaEfectivo(ids);
  } else if (conf && conf.codigo === 'SALDO_CLIENTE') {
    ant.aplicar_saldo = window.confirm(textoSaldo(conf));
  } else if (conf && conf.codigo === 'X_CAMBIO') {
    avisoX = 'El anticipo cambió desde que lo viste' + (conf.X != null ? ': ahora es ' + $p(conf.X) : '') + '.\n\n';
    if (conf.X != null) xServidor = cents(conf.X);
  }
  const previa = async () => {
    const q = ['folio=' + encodeURIComponent(a.folio || ''), 'origen=' + origen];
    if (origen === 'pwa' && a.id) q.push('pedido_id=' + encodeURIComponent(a.id));
    if (extra.soid) q.push('soid=' + encodeURIComponent(extra.soid));
    if (extra.line_ids) q.push('line_ids=' + extra.line_ids.join(','));
    if (typeof ant.aplicar_saldo === 'boolean') q.push('aplicar_saldo=' + (ant.aplicar_saldo ? 'true' : 'false'));
    if (ant.ignorar_por_validar) q.push('ignorar_por_validar=1');
    return api(BASE + '/entrega/previa?' + q.join('&'));
  };
  const previo = () => ({ ...ant, ...(dec.efectivo_ahora ? { efectivo_ahora: true } : {}) });
  let pv = null;
  for (let vuelta = 0; vuelta < 4; vuelta++) {
    let r;
    try { r = await previa(); } catch (e) {
      // 22-oct-2026: se va por paquetería y nadie capturó lo cobrado del envío: no se manda a caja (el servidor
      // también lo detiene); se dice aquí, antes del "¿Enviar?".
      if (errCod(e) === 'ENVIO_SIN_COBRO') { alert(errTxt(e)); return { ok: false }; }
      r = null;
    }
    if (!r) {
      if (pv) break;   // ya hay una previa buena: se usa esa
      if (conf) {      // el control está prendido (hubo 409) y no se puede confirmar el total: no se manda a ciegas
        alert('No pude consultar el anticipo para confirmarte el total. Intenta de nuevo en un momento.');
        return { ok: false };
      }
      // apagado (503 APAGADO), sin la ruta o sin conexión: como hoy; el servidor decide y falla cerrado
      return { ok: true, confirmado: false, body: Object.keys(ant).length ? { anticipo: ant } : undefined, previo: previo() };
    }
    pv = r;
    const b = pv.bloqueo && COD_DECIDIR.includes(pv.bloqueo.codigo) ? pv.bloqueo : null;
    let pregunto = false;
    // 1) pago por validar: esperar a María o cobrar completo
    const pvi = infoPorValidar(b && b.codigo === 'POR_VALIDAR' && lista(b.pagos).length ? b.pagos : pv.por_validar);
    if ((pvi.n > 0 || (b && b.codigo === 'POR_VALIDAR')) && !ant.ignorar_por_validar) {
      if (!window.confirm(textoPorValidar(pvi.n > 0 ? pvi : { n: 1, c: 0, pagos: lista(b && b.pagos) }))) return { ok: false };
      ant.ignorar_por_validar = true; pregunto = true;
    }
    // 2) efectivo validado que la cajera no ha cobrado (si llegan los números, se cancela ese ticket ANTICIPO)
    const efi = infoEfectivo(b && b.codigo === 'EFECTIVO_SIN_COBRAR' ? b.asignaciones : pv.efectivo_sin_cobrar);
    if (efi.c > 0 || efi.asigs.length) {
      const ids = idsAsig(efi.asigs).filter(id => !(ant.cancelar_efectivo || []).includes(id));
      if (!dec.efectivo_ahora) {
        if (!window.confirm(textoEfectivo(efi))) return { ok: false };
        dec.efectivo_ahora = true; pregunto = true;
      }
      if (ids.length) sumaEfectivo(ids);
    }
    // 3) saldo a favor de la clienta en otros folios: con la respuesta se vuelve a pedir la previa (cambia X)
    const fuenteSaldo = b && b.codigo === 'SALDO_CLIENTE' ? b : pv.saldo_cliente;
    if (infoSaldo(fuenteSaldo).c > 0 && typeof ant.aplicar_saldo !== 'boolean') {
      ant.aplicar_saldo = window.confirm(textoSaldo(fuenteSaldo));
      pregunto = true;
    }
    if (!pregunto) break;
  }
  // ¿El X de la previa es el real? No, si la previa no pudo simular (bloqueo): entonces se confirma lo que
  // se sabe y, si el servidor calcula otro anticipo, contesta X_CAMBIO y se vuelve a preguntar con el real.
  const fiable = !(pv && pv.bloqueo);
  const T = cents(pv.T);
  const X = xServidor != null ? xServidor : cents(pv.X);
  ant.x_esperado = pesos(X);
  const cola = Array.isArray(pv.en_cola) ? pv.en_cola.reduce((s, x) => s + cents(x.monto), 0) : cents(pv.en_cola);
  const notas = [];
  if (cola > 0) notas.push('Hay ' + $c(cola) + ' validado que aún no entra a caja: se registra al enviar.');
  if (pv.por_resolver_n > 0) notas.push('Ojo: esta clienta tiene ' + pv.por_resolver_n + ' pago(s) en Por resolver; los decide María.');
  if (dec.efectivo_ahora) notas.push('El efectivo pendiente se cobra en este ticket (se cancela su ticket ANTICIPO).');
  const quien = '¿Enviar a caja el pedido ' + (a.folio || '') + ' de ' + (a.client_name || 'sin cliente') + '?\n\n';
  const pie = notas.length ? '\n\n' + notas.join('\n') : '';
  const listo = (confirmado) => ({ ok: true, confirmado, body: { anticipo: ant }, previo: previo(), T: pesos(T), X: pesos(X) });
  // 22-oct-2026: con envío por paquetería el total ya lo incluye (línea "Envío" del ticket): se dice cuál.
  const conEnvio = pv.envio && cents(pv.envio.monto) > 0 ? ' (incluye ' + (pv.envio.nombre || 'el envío') + ' ' + $p(pv.envio.monto) + ')' : '';
  if (X > 0) {
    if (!window.confirm(avisoX + quien + 'Total ' + $c(T) + conEnvio + ' · anticipo ya pagado −' + $c(X) + ' · en caja se cobra ' + $c(Math.max(0, T - X)) + pie)) return { ok: false };
    return listo(true);
  }
  if (!fiable || avisoX || notas.length || conEnvio) {
    const sinAnt = fiable && xServidor == null && cola === 0 ? ' (sin anticipo: se cobra completo)' : '. Si al enviar resulta un anticipo, te lo confirmo antes de crear el ticket.';
    if (!window.confirm(avisoX + quien + 'Total ' + $c(T) + conEnvio + sinAnt + pie)) return { ok: false };
    return listo(true);
  }
  return listo(false);
}

// ════════════════════════════════════════════════════════════════════════
// Piezas comunes: hoja, modal, archivo con token, comprobante grande
// ════════════════════════════════════════════════════════════════════════
function Hoja({ titulo, sub, onClose, children, pie, datos }) {
  return (
    <div className="pa pa-sheet" role="dialog" aria-modal="true" aria-label={titulo} data-pa={datos}>
      <div className="pa-top"><button type="button" className="pa-x" aria-label="Cerrar" onClick={onClose}>×</button><div className="pa-t">{titulo}{sub ? <small>{sub}</small> : null}</div></div>
      {children}
      {pie ? <div className="pa-foot">{pie}</div> : null}
    </div>
  );
}
function Modal({ titulo, sub, onClose, children, pie, datos }) {
  return (
    <div className="pa pa-modal" role="dialog" aria-modal="true" aria-label={titulo} data-pa={datos}>
      <div className="pa-card">
        <div className="pa-top"><button type="button" className="pa-x" aria-label="Cerrar" onClick={onClose}>×</button><div className="pa-t">{titulo}{sub ? <small>{sub}</small> : null}</div></div>
        <div className="pa-body">{children}</div>
        {pie ? <div className="pa-foot">{pie}</div> : null}
      </div>
    </div>
  );
}

function ImgAuth({ url, alt }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let vivo = true, h = null;
    setSrc(null); setErr('');
    if (!url) return undefined;
    verArchivo(url).then(r => { if (vivo) { h = r; setSrc(r.src); } else r.liberar(); })
      .catch(e => { if (vivo) setErr((e && e.status === 404) ? 'sin archivo' : 'no se pudo ver'); });
    return () => { vivo = false; if (h) h.liberar(); };
  }, [url]);
  if (err) return <span className="pa-gris">{err}</span>;
  if (!src) return <span className="pa-gris">…</span>;
  return <img src={src} alt={alt || ''} />;
}

// Comprobante grande con zoom; si es PDF, la vista previa (la hace el bot) y "Abrir PDF" por blob.
function VerComprobante({ archivoId }) {
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState('');
  const [visor, setVisor] = useState(false);
  const [zoom, setZoom] = useState(false);
  useEffect(() => {
    let vivo = true, h = null;
    setDoc(null); setErr('');
    if (!archivoId) return undefined;
    verArchivo(urlArchivo(archivoId, 'completo')).then(r => { if (vivo) { h = r; setDoc(r); } else r.liberar(); })
      .catch(e => { if (vivo) setErr(errTxt(e)); });
    return () => { vivo = false; if (h) h.liberar(); };
  }, [archivoId]);
  // Visor abierto: Esc lo cierra y el fondo no se desplaza detrás.
  useEffect(() => {
    if (!visor) return undefined;
    const tecla = e => { if (e.key === 'Escape') { setVisor(false); setZoom(false); } };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', tecla);
    return () => { window.removeEventListener('keydown', tecla); document.body.style.overflow = prev; };
  }, [visor]);
  if (!archivoId) return <div className="pa-note">Sin comprobante (efectivo o se reportó sin archivo).</div>;
  if (err) return <div className="pa-note bad">No se pudo ver el comprobante: {err}</div>;
  if (!doc) return <div className="pa-comp"><span className="pa-gris">Cargando comprobante…</span></div>;
  const esPdf = /pdf/i.test(doc.mime || '');
  return (
    <>
      <button type="button" className="pa-comp" onClick={() => setVisor(true)} aria-label="Ver el comprobante en grande" data-pa="comprobante">
        {esPdf ? <ImgAuth url={urlArchivo(archivoId, 'preview')} alt="Vista previa del PDF" /> : <img src={doc.src} alt="Comprobante" />}
      </button>
      {esPdf && <button type="button" className="pa-btn sm ghost full" style={{ marginTop: 6 }} onClick={() => setVisor(true)}>Abrir PDF</button>}
      {visor && createPortal(
        <div className="pa pa-visor" role="dialog" aria-modal="true" aria-label="Comprobante">
          <div className="pa-top">
            <button type="button" className="pa-x" aria-label="Cerrar" onClick={() => { setVisor(false); setZoom(false); }}>×</button>
            <div className="pa-t">Comprobante</div>
            {!esPdf && <button type="button" className="pa-btn sm" onClick={() => setZoom(z => !z)}>{zoom ? 'Alejar' : 'Acercar'}</button>}
          </div>
          <div className="pa-visor-b">
            {esPdf
              ? <object data={doc.src} type="application/pdf" aria-label="PDF del comprobante"><a href={doc.src} download="comprobante.pdf" style={{ color: '#FFFFFF', padding: 20, display: 'block' }}>Descargar el PDF</a></object>
              : <img src={doc.src} alt="Comprobante" className={zoom ? 'zoom' : ''} onClick={() => setZoom(z => !z)} />}
          </div>
        </div>, document.body
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Formulario de validación (lo usan "Validar" de la bandeja y "validar ahora" al reportar)
// ════════════════════════════════════════════════════════════════════════
// fecha0 (22-oct-2026): fecha leída del comprobante; se sigue hasta que María la toque (como monto0). Sin ella, hoy.
// leido = { monto, forma, fecha, ref }: qué campos vienen LEÍDOS del comprobante (se marca junto al campo mientras
// no se toque). "Lo vi en el estado de cuenta" nunca se prellena.
function FormValidacion({ api, partnerId, folioSugerido, candidatos, monto0, forma0, ref0, fecha0, leido, conVisto, onCambio }) {
  const [monto, setMonto] = useState(monto0 != null && monto0 !== '' ? String(monto0) : '');
  const [montoTocado, setMontoTocado] = useState(false);
  const [forma, setForma] = useState(FORMAS_VAL.some(f => f[0] === forma0) ? forma0 : '');
  const [formaTocada, setFormaTocada] = useState(false);
  const [fecha, setFecha] = useState(fecha0 || hoyMx());
  const [fechaTocada, setFechaTocada] = useState(false);
  const [ref, setRef] = useState(ref0 || '');
  const [refTocada, setRefTocada] = useState(false);
  const lei = leido || {};
  const marca = (si) => (si ? <span className="pa-gris" data-pa="leido" style={{ display: 'block', textTransform: 'none', letterSpacing: 0, fontWeight: 400, marginTop: 2 }}>Leído del comprobante: confírmalo contra el banco</span> : null);
  const [visto, setVisto] = useState(false);
  const [folios, setFolios] = useState(null);
  const [errF, setErrF] = useState('');
  const [rep, setRep] = useState({});
  const [tocado, setTocado] = useState(false);
  // lo reportado puede cambiar mientras el formulario está abierto (validar ahora): se sigue hasta que se toque
  useEffect(() => { if (!montoTocado) setMonto(monto0 != null && monto0 !== '' ? String(monto0) : ''); }, [monto0]); // eslint-disable-line
  useEffect(() => { if (!formaTocada && FORMAS_VAL.some(f => f[0] === forma0)) setForma(forma0); }, [forma0]); // eslint-disable-line
  useEffect(() => { if (!fechaTocada && fecha0) setFecha(fecha0); }, [fecha0]); // eslint-disable-line
  useEffect(() => { if (!refTocada && ref0 != null) setRef(ref0); }, [ref0]); // eslint-disable-line
  useEffect(() => {
    let vivo = true;
    const base = [folioSugerido].concat(lista(candidatos)).filter(Boolean);
    const armar = (vivos) => {
      const por = {};
      vivos.forEach(f => { por[f.folio] = f; });
      const out = vivos.slice();
      base.forEach(fo => { if (!por[fo]) out.unshift({ folio: fo, cerrado: true }); });
      out.sort((x, y) => (x.folio === folioSugerido ? -1 : y.folio === folioSugerido ? 1 : 0));
      return out;
    };
    if (!partnerId) { setFolios(armar([])); return undefined; }
    api(BASE + '/folios-vivos?partner_id=' + encodeURIComponent(partnerId))
      .then(r => {
        if (!vivo) return;
        const vivos = lista(r && r.folios).map(f => ({ folio: f.folio, total: f.total_mxn != null ? f.total_mxn : (f.resumen && f.resumen.importe), resta: f.resumen ? f.resumen.resta : null, fecha: f.fecha_recoleccion, envio: f.envio || null }));
        setFolios(armar(vivos));
      })
      .catch(e => { if (vivo) { setErrF(errTxt(e)); setFolios(armar([])); } });
    return () => { vivo = false; };
    /* eslint-disable-next-line */
  }, [partnerId, folioSugerido]);
  const cM = aCent(monto);
  const repEf = tocado ? rep : { [folioSugerido]: isFinite(cM) && cM > 0 ? String(pesos(cM)) : '' };
  const filas = (folios || [{ folio: folioSugerido }]).map(f => ({ ...f, txt: repEf[f.folio] || '', c: aCent(repEf[f.folio] || '') }));
  const suma = filas.reduce((s, f) => s + (isFinite(f.c) && f.c > 0 ? f.c : 0), 0);
  const malos = filas.some(f => f.txt !== '' && !isFinite(f.c));
  const dif = (isFinite(cM) ? cM : 0) - suma;
  const efectivo = forma === 'efectivo';
  const fechaEf = efectivo ? hoyMx() : fecha;
  const fechaOk = /^\d{4}-\d{2}-\d{2}$/.test(fechaEf) && fechaEf >= restaDias(hoyMx(), 60) && fechaEf <= hoyMx();
  let motivo = '';
  if (!(isFinite(cM) && cM > 0)) motivo = 'Escribe el monto que entró.';
  else if (!forma) motivo = 'Elige la forma de pago.';
  else if (!fechaOk) motivo = 'La fecha del depósito debe ser de hoy o de los últimos 60 días.';
  else if (conVisto && (forma === 'transferencia' || forma === 'mercado_pago') && !visto) motivo = 'Marca que lo viste en el estado de cuenta.';
  else if (forma === 'tarjeta' && !ref.trim()) motivo = 'Con tarjeta escribe la autorización en Referencia.';
  else if (malos) motivo = 'Revisa los montos del reparto.';
  else if (dif !== 0) motivo = dif > 0 ? 'Faltan ' + $c(dif) + ' por repartir.' : 'Sobran ' + $c(-dif) + ' en el reparto.';
  const asignaciones = filas.filter(f => isFinite(f.c) && f.c > 0).map(f => ({ folio: f.folio, monto: pesos(f.c) }));
  const firma = JSON.stringify([monto, forma, fechaEf, ref, visto, asignaciones, motivo]);
  useEffect(() => {
    if (onCambio) onCambio({
      listo: !motivo, motivo,
      datos: { monto_real: pesos(isFinite(cM) ? cM : 0), forma, fecha_deposito: fechaEf, ...(ref.trim() ? { referencia: ref.trim() } : {}), visto_en_banco: conVisto ? !!visto : true, asignaciones },
    });
    /* eslint-disable-next-line */
  }, [firma]);
  const ponRep = (fo, v) => { const base = tocado ? rep : repEf; setRep({ ...base, [fo]: v }); setTocado(true); };
  return (
    <div data-pa="form-validacion">
      <label className="pa-lbl" htmlFor="pa-v-monto">Monto que entró{marca(lei.monto && !montoTocado)}</label>
      <input id="pa-v-monto" className="pa-in" inputMode="decimal" value={monto} onChange={e => { setMonto(e.target.value); setMontoTocado(true); }} placeholder="0.00" />
      <span className="pa-lbl">Forma{marca(lei.forma && !formaTocada)}</span>
      <div className="pa-seg" role="group" aria-label="Forma de pago">
        {FORMAS_VAL.map(([v, t]) => <button key={v} type="button" className={forma === v ? 'on' : ''} aria-pressed={forma === v} onClick={() => { setForma(v); setFormaTocada(true); }}>{t}</button>)}
      </div>
      {efectivo
        ? <div className="pa-note">Efectivo: validar significa "autorizo que la caja cobre {isFinite(cM) && cM > 0 ? $c(cM) : '$X'}". La fecha es la de hoy.</div>
        : (<><label className="pa-lbl" htmlFor="pa-v-fecha">Fecha del depósito{marca(lei.fecha && !fechaTocada)}</label>
          <input id="pa-v-fecha" className="pa-in" type="date" value={fecha} min={restaDias(hoyMx(), 60)} max={hoyMx()} onChange={e => { setFecha(e.target.value); setFechaTocada(true); }} /></>)}
      <label className="pa-lbl" htmlFor="pa-v-ref">Referencia {forma === 'tarjeta' ? '(autorización, obligatoria)' : '(opcional)'}{marca(lei.ref && !refTocada)}</label>
      <input id="pa-v-ref" className="pa-in" value={ref} onChange={e => { setRef(e.target.value); setRefTocada(true); }} placeholder="Clave de rastreo o autorización" />
      {conVisto && (forma === 'transferencia' || forma === 'mercado_pago') && (
        <label className="pa-ck"><input type="checkbox" checked={visto} onChange={e => setVisto(e.target.checked)} /><span>Lo vi en el estado de cuenta</span></label>
      )}
      <span className="pa-lbl">Reparto entre los apartados de la clienta</span>
      {errF && <div className="pa-note warn">No se pudieron leer los otros apartados ({errF}): solo se puede asignar a {folioSugerido}.</div>}
      {!folios ? <div className="pa-gris">Cargando apartados…</div> : (
        <div className="pa-rep">
          {filas.map(f => {
            const rC = f.resta != null ? cents(f.resta) : null;
            const pasa = rC != null && isFinite(f.c) && f.c > rC;
            return (
              <div key={f.folio} className="pa-rep-r" data-folio={f.folio}>
                <div className="tx"><b>{f.folio}</b>
                  <small>{f.cerrado ? 'Cerrado o no vivo: quedará en Por resolver' : ('Total ' + (f.total != null ? $p(f.total) : '—') + ' · resta ' + (rC != null ? $c(rC) : '—'))}
                    {!f.cerrado && f.envio && (f.envio.falta_cobro ? ' · se va por paquetería: falta capturar lo cobrado del envío' : ' · incluye ' + (f.envio.nombre || 'el envío') + ' ' + $p(f.envio.monto))}</small></div>
                <input inputMode="decimal" aria-label={'Monto para ' + f.folio} value={f.txt} onChange={e => ponRep(f.folio, e.target.value)} placeholder="0" />
                {pasa && <div className="av">Rebasa lo que resta por {$c(f.c - rC)}; al entregar quedará como saldo a favor.</div>}
              </div>
            );
          })}
        </div>
      )}
      <div className={'pa-cuadre ' + (dif === 0 && !malos ? 'ok' : 'bad')} data-pa="cuadre">
        {malos ? 'Hay un monto mal escrito' : dif === 0 ? 'Cuadra' : dif > 0 ? 'Faltan ' + $c(dif) : 'Sobran ' + $c(-dif)}
      </div>
    </div>
  );
}

const textoDuplicados = (posibles) => lista(posibles).map(p => codAB(p.codigo) + ' · ' + (p.folio_sugerido || p.folio || '') + ' · ' + $p(p.monto_real != null ? p.monto_real : p.monto_reportado) + (p.referencia ? ' · ref ' + p.referencia : '') + (p.fecha_deposito ? ' · ' + p.fecha_deposito : '')).join('\n');
// Duplicado REAL (mismo folio de banco): de la lectura (pago.duplicado.con) o del 409 DUPLICADO_BANCO (duplicados).
const ESTADO_PAGO = { por_validar: 'por validar', validado: 'VALIDADO', rechazado: 'rechazado' };
const textoDupBanco = (lst) => lista(lst).map(o => codAB(o.codigo) + ' · ' + (o.folio || o.folio_sugerido || '—') + (o.misma_clienta === false ? ' (otra clienta)' : '') +
  ' · ' + (ESTADO_PAGO[o.estado] || o.estado || '') + (o.importe != null ? ' · ' + $p(o.importe) : '') + (o.folio_banco ? ' · folio banco ' + o.folio_banco : '')).join('\n');

// "Leído del comprobante" (validar y detalle). Muestra lo que leyó la IA y ofrece "Leer otra vez" a quien valida.
function BloqueLectura({ api, pago, showToast }) {
  const p = pago || {};
  const l = p.lectura;
  const [releyendo, setReleyendo] = useState(false);
  const [msg, setMsg] = useState('');
  if (!l) return null;
  const releer = async () => {
    if (releyendo) return;
    setReleyendo(true); setMsg('');
    try {
      const r = await api(BASE + '/pago/' + encodeURIComponent(p.id) + '/releer', { method: 'POST', body: {} });
      const t = (r && r.mensaje) || (r && r.programada ? 'Se vuelve a leer en unos segundos.' : 'No se pudo programar.');
      setMsg(r && r.programada ? t + ' Cierra y vuelve a abrir el pago para ver la lectura nueva.' : t);
      if (showToast && r && r.programada) showToast('Leyendo otra vez ' + codAB(p.codigo));
    } catch (e) { setMsg(errTxt(e)); }
    finally { setReleyendo(false); }
  };
  const boton = (txt) => <button type="button" className="pa-btn sm ghost" data-pa="releer" disabled={releyendo} onClick={releer} style={{ marginTop: 8 }}>{releyendo ? '…' : txt}</button>;
  if (l.estado === 'pendiente' || l.estado === 'leyendo') {
    return <div className="pa-note" data-pa="lectura-bloque" data-estado={l.estado}>Leyendo el comprobante… (unos segundos). Puedes validar igual, como siempre.</div>;
  }
  if (l.estado === 'fallida') {
    return (
      <div className="pa-note warn" data-pa="lectura-bloque" data-estado="fallida">
        No se pudo leer el comprobante{l.error_tipo === 'saldo' || l.error_tipo === 'llave' ? ' (la lectura con IA no está disponible)' : l.error_tipo === 'ilegible' ? ' (no se lee bien)' : ''}{l.reintenta ? '; se reintenta solo' : ''}. Valídalo como siempre, viendo la imagen.
        <div>{boton('Leer otra vez')}</div>
        {msg && <div style={{ marginTop: 6 }}>{msg}</div>}
      </div>
    );
  }
  if (l.estado === 'no_comprobante') {
    return (
      <div className="pa-note warn" data-pa="lectura-bloque" data-estado="no_comprobante">
        La lectura dice que esto NO parece un comprobante de pago{l.notas ? ' (' + l.notas + ')' : ''}. Revísalo bien antes de validar.
        <div>{boton('Leer otra vez')}</div>
        {msg && <div style={{ marginTop: 6 }}>{msg}</div>}
      </div>
    );
  }
  const cta = CUENTA_TXT[l.cuenta_oficial] || CUENTA_TXT.sin_dato;
  const COLOR = { bad: { color: 'var(--err)', fontWeight: 600 }, warn: { color: 'var(--warn)' }, ok: { color: 'var(--ok)' }, ap: { color: 'var(--ap)', fontWeight: 600 } };
  const fila = (k, v, clase) => (v == null || v === '' ? null : <div key={k} style={COLOR[clase]} data-pa={clase ? 'cuenta-' + clase : undefined}><b>{k}:</b> {v}</div>);
  return (
    <div className="pa-note" data-pa="lectura-bloque" data-estado="leido">
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Leído del comprobante (confírmalo contra el banco)</div>
      {fila('Importe', l.importe != null ? $p(l.importe) + (l.moneda && l.moneda !== 'MXN' ? ' ' + l.moneda : '') : 'no se lee')}
      {fila('Fecha y hora', l.fecha_hora ? fechaMx(l.fecha_hora) + (l.con_hora ? ' ' + horaMx(l.fecha_hora) : ' (sin hora)') : 'no se lee')}
      {fila('Banco', [l.banco_emisor, l.banco_destino ? '→ ' + l.banco_destino : ''].filter(Boolean).join(' '))}
      {fila('Tipo', TIPO_LECT[l.tipo] || l.tipo)}
      {fila('Folio del banco', l.folio)}
      {fila('Clave de rastreo', l.clave_rastreo)}
      {fila('Autorización', l.autorizacion)}
      {fila('Concepto', l.concepto)}
      {fila('Cuenta destino', (l.cuenta_destino_ult4 ? '…' + l.cuenta_destino_ult4 + ': ' : '') + cta[1], cta[0])}
      {fila('Beneficiario', l.beneficiario)}
      {ESTADO_OP_MAL[l.estado_operacion] && <div style={{ color: 'var(--err)', fontWeight: 600 }}>El comprobante dice {ESTADO_OP_MAL[l.estado_operacion]}: el dinero puede no haber entrado.</div>}
      {l.confianza === 'baja' && <div style={{ color: 'var(--warn)' }}>Lectura dudosa: revisa cada dato en la imagen.</div>}
      {l.notas && <div className="pa-gris">Nota de la lectura: {l.notas}</div>}
      <div>{boton('Leer otra vez')}</div>
      {msg && <div style={{ marginTop: 6 }}>{msg}</div>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Registrar un pago (vendedora, María, o "otro folio" desde la bandeja)
// ════════════════════════════════════════════════════════════════════════
export function ModalReportarPago({ api, apartado, puedeValidar, onClose, onHecho, showToast }) {
  ponerCSS();
  const ap = apartado || {};
  const folioFijo = !!ap.folio;
  const [folioTxt, setFolioTxt] = useState(ap.folio || '');
  const [info, setInfo] = useState(ap.pagos || null);
  const [buscando, setBuscando] = useState(false);
  const [monto, setMonto] = useState('');
  const [forma, setForma] = useState('');
  const [arch, setArch] = useState(null);
  const [prep, setPrep] = useState(false);
  const [ref, setRef] = useState('');
  const [nota, setNota] = useState('');
  const [valYa, setValYa] = useState(false);
  const [val, setVal] = useState(null);
  const [dup, setDup] = useState(null);
  const [confDup, setConfDup] = useState(false);
  const [dupB, setDupB] = useState(null);         // 22-oct-2026: 409 DUPLICADO_BANCO (mismo folio de banco)
  const [confB, setConfB] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const [hecho, setHecho] = useState(null);
  // 20-oct-2026: llave de idempotencia por cada vez que se abre el modal (no cambia entre reintentos). Si el
  // primer envío sí llegó pero la respuesta se perdió (timeout, iPhone que bloquea la pantalla), el reintento
  // devuelve el MISMO pago: el efectivo sin foto no tiene otra forma de deduplicarse.
  const [llave] = useState(() => (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID()
    : (Date.now().toString(16) + '-' + Math.random().toString(16).slice(2) + Math.random().toString(16).slice(2)));
  const folio = folioFijo ? ap.folio : normFolio(folioTxt);
  const efectivo = forma === 'efectivo';
  const modoB = efectivoAlReportar();
  const puedeValidarAqui = !!puedeValidar && !(efectivo && modoB);
  const partnerId = (info && info.partner_id) || ap.client_id || null;
  const cM = aCent(monto);
  const buscarFolio = async () => {
    const fo = normFolio(folioTxt);
    if (!fo) return;
    setBuscando(true); setError(''); setInfo(null);
    try {
      const r = await api(BASE + '/resumen?folios=' + encodeURIComponent(fo));
      const x = r && r.resumen && r.resumen[fo];
      if (!x) setError('No encontré el folio ' + fo + '.'); else setInfo(x);
    } catch (e) { setError(errTxt(e)); }
    finally { setBuscando(false); }
  };
  const tomarArchivo = async (file) => {
    if (!file) return;
    setPrep(true); setError('');
    try { setArch(await comprimirComprobante(file)); }
    catch (e) { setArch(null); setError((e && e.message) || 'No se pudo preparar el comprobante'); }
    finally { setPrep(false); }
  };
  let falta = '';
  if (!folio) falta = 'Escribe el folio del apartado.';
  else if (!(isFinite(cM) && cM > 0)) falta = 'Escribe cuánto pagó.';
  else if (!forma) falta = 'Elige cómo pagó.';
  else if (!efectivo && !arch) falta = 'Falta la foto o el PDF del comprobante.';
  else if (valYa && puedeValidarAqui && !(val && val.listo)) falta = (val && val.motivo) || 'Completa la validación.';
  const enviar = async () => {
    if (falta || enviando || prep) return;
    setEnviando(true); setError('');
    const body = { folio, monto_reportado: pesos(cM), forma_reportada: forma, llave };
    if (ref.trim()) body.referencia = ref.trim();
    if (nota.trim()) body.nota = nota.trim();
    if (arch) body.archivo = { b64: arch.b64, mime: arch.mime, ...(arch.mini_b64 ? { mini_b64: arch.mini_b64 } : {}), sha256_original: arch.sha256_original };
    if (valYa && puedeValidarAqui && val) body.validar_ya = { ...val.datos, ...(confDup ? { confirmar_duplicado: true } : {}), ...(confB ? { confirmar_duplicado_banco: true } : {}) };
    try {
      const r = await api(BASE + '/reportar', { method: 'POST', body });
      setHecho(r || {});
      if (showToast) showToast('Pago registrado ' + codAB(r && r.pago && r.pago.codigo));
    } catch (e) {
      // Un error con código del servidor = no se creó nada. Sin respuesta (NO_DISPONIBLE, red cortada) PUEDE que
      // sí se haya registrado: reintentar es seguro porque va con la misma llave (no se duplica).
      if (errCod(e) === 'DUPLICADO_BANCO') { setDupB(lista(e.data.duplicados)); setError('Ese folio de banco ya está en otro pago. Revisa abajo.'); }
      else if (errCod(e) === 'DUPLICADO_POSIBLE') { setDup(lista(e.data.posibles)); setError('Parece un pago que ya se validó. Revisa abajo.'); }
      else if (!errCod(e) || errCod(e) === 'NO_DISPONIBLE') setError('No tuve respuesta: puede que sí se haya registrado. Vuelve a tocar Registrar: no se duplica.' + (errTxt(e) && errTxt(e) !== 'error' ? ' (' + errTxt(e) + ')' : ''));
      else setError(errTxt(e));
    } finally { setEnviando(false); }
  };
  if (hecho) {
    const p = hecho.pago || {};
    const v = hecho.validacion;
    const vOk = v && v.ok !== false && v.success !== false && !v.error;   // el servidor responde {success:false, codigo, error}
    return (
      <Modal titulo={'Pago ' + codAB(p.codigo)} sub={folio} onClose={onHecho || onClose} datos="reportar-hecho"
        pie={<button type="button" className="pa-btn" onClick={onHecho || onClose}>Listo</button>}>
        {hecho.ya_existia
          ? <div className="pa-note warn">Ese pago ya estaba registrado ({codAB(p.codigo)}{p.monto_reportado != null ? ' · ' + $p(p.monto_reportado) : ''}). No se duplicó.</div>
          : vOk
            ? <div className="pa-note ok">{v.mensaje || 'Validado.'}</div>
            : efectivo && modoB
              ? <div className="pa-note ok">Listo ({codAB(p.codigo)}). La clienta paga en caja: cuando la cajera cobre el ticket ANTICIPO queda validado. Todavía NO es pago confirmado.</div>
              : <div className="pa-note ok">Listo ({codAB(p.codigo)}). María lo valida; todavía NO es pago confirmado.</div>}
        {v && !vOk && <div className="pa-note bad">No se pudo validar ahora: {v.error || v.mensaje || 'error'}. Quedó POR VALIDAR: termínalo en la bandeja de pagos.
          {v.codigo === 'DUPLICADO_BANCO' && lista(v.duplicados).length > 0 && <pre data-pa="duplicado-banco" style={{ whiteSpace: 'pre-wrap', margin: '6px 0 0', font: 'inherit' }}>{textoDupBanco(v.duplicados)}</pre>}</div>}
        {hecho.aviso && <div className="pa-note warn">{hecho.aviso}</div>}
        {hecho.posible_reuso && <div className="pa-note bad">Ojo: ese mismo comprobante ya se había registrado en {hecho.posible_reuso.folio} ({codAB(hecho.posible_reuso.codigo)}). María lo revisa.</div>}
        <div className="pa-note">Lo que la clienta paga al recoger se cobra en el ticket de entrega, NO aquí.</div>
      </Modal>
    );
  }
  const titulo = 'Registrar pago' + (folio ? ' · ' + folio : '') + (ap.client_name ? ' · ' + ap.client_name : '');
  return (
    <Modal titulo={titulo} onClose={onClose} datos="reportar"
      pie={<><button type="button" className="pa-btn ghost" onClick={onClose}>Cancelar</button>
        <button type="button" className="pa-btn" disabled={!!falta || enviando || prep} onClick={enviar}>{enviando ? 'Enviando…' : valYa && puedeValidarAqui ? 'Registrar y validar' : 'Registrar pago'}</button></>}>
      <div className="pa-note warn"><b>Lo que la clienta paga al recoger se cobra en el ticket de entrega, NO aquí.</b></div>
      {!folioFijo && (
        <>
          <label className="pa-lbl" htmlFor="pa-r-folio">Folio del apartado</label>
          <div className="pa-fila">
            <input id="pa-r-folio" className="pa-in" value={folioTxt} onChange={e => { setFolioTxt(e.target.value); setInfo(null); }} onBlur={buscarFolio} placeholder="WA-1044" autoCapitalize="characters" />
            <button type="button" className="pa-btn ghost" style={{ flex: '0 0 auto' }} onClick={buscarFolio} disabled={buscando}>{buscando ? '…' : 'Buscar'}</button>
          </div>
          {info && <div className="pa-sub">Apartado de {$p(info.importe)} · resta {$p(info.resta)}{info.vivo === false ? ' · CERRADO: el pago quedará en Por resolver' : ''}</div>}
        </>
      )}
      <label className="pa-lbl" htmlFor="pa-r-monto">¿Cuánto pagó?</label>
      <input id="pa-r-monto" className="pa-in" inputMode="decimal" value={monto} onChange={e => setMonto(e.target.value)} placeholder="0.00" />
      <span className="pa-lbl">¿Cómo pagó?</span>
      <div className="pa-seg" role="group" aria-label="¿Cómo pagó?">
        {FORMAS_REP.map(([v, t]) => <button key={v} type="button" className={forma === v ? 'on' : ''} aria-pressed={forma === v} onClick={() => setForma(v)}>{t}</button>)}
      </div>
      {efectivo && (modoB
        ? <div className="pa-note warn">La clienta paga en CAJA. Al registrar esto, la cajera recibe un ticket ANTICIPO por {isFinite(cM) && cM > 0 ? $c(cM) : '$X'} para cobrarlo al recibir el billete; ese cobro es la validación. Nadie recibe billetes fuera de caja.</div>
        : <div className="pa-note warn">La clienta paga en CAJA. María valida y la cajera recibe un ticket ANTICIPO por {isFinite(cM) && cM > 0 ? $c(cM) : '$X'} para cobrarlo al recibir el billete. Nadie recibe billetes fuera de caja.</div>)}
      <span className="pa-lbl">Foto del comprobante{efectivo ? ' (opcional)' : ''}</span>
      <label className="pa-file" data-pa="archivo">
        <input type="file" accept="image/*,application/pdf" onChange={e => { tomarArchivo(e.target.files && e.target.files[0]); e.target.value = ''; }} />
        {arch && arch.mini_b64 ? <span className="pa-mini"><img src={'data:image/jpeg;base64,' + arch.mini_b64} alt="Comprobante elegido" /></span> : null}
        <span>{prep ? 'Preparando…' : arch ? (arch.mime === 'application/pdf' ? 'PDF listo · ' : 'Foto lista · ') + Math.round(arch.bytes / 1024) + ' KB · tocar para cambiar' : 'Tomar foto, galería o PDF'}</span>
      </label>
      <label className="pa-lbl" htmlFor="pa-r-ref">Referencia o clave de rastreo (opcional)</label>
      <input id="pa-r-ref" className="pa-in" value={ref} onChange={e => setRef(e.target.value)} />
      <label className="pa-lbl" htmlFor="pa-r-nota">Nota (opcional)</label>
      <textarea id="pa-r-nota" className="pa-in" rows={2} value={nota} onChange={e => setNota(e.target.value)} />
      {puedeValidarAqui && (
        <label className="pa-ck"><input type="checkbox" checked={valYa} onChange={e => setValYa(e.target.checked)} /><span>Ya lo vi en el banco / lo autorizo: validar ahora</span></label>
      )}
      {valYa && puedeValidarAqui && (
        <FormValidacion api={api} partnerId={partnerId} folioSugerido={folio} candidatos={[]} monto0={isFinite(cM) && cM > 0 ? String(pesos(cM)) : ''}
          forma0={forma} ref0={ref} conVisto={false} onCambio={setVal} />
      )}
      {dup && (
        <div className="pa-note bad">
          Posible duplicado de:<pre style={{ whiteSpace: 'pre-wrap', margin: '6px 0', font: 'inherit' }}>{textoDuplicados(dup)}</pre>
          <label className="pa-ck"><input type="checkbox" checked={confDup} onChange={e => setConfDup(e.target.checked)} /><span>Sí es otro pago</span></label>
        </div>
      )}
      {dupB && dupB.length > 0 && (
        <div className="pa-note bad" data-pa="duplicado-banco">
          Duplicado: mismo folio de banco que:<pre style={{ whiteSpace: 'pre-wrap', margin: '6px 0', font: 'inherit' }}>{textoDupBanco(dupB)}</pre>
          <label className="pa-ck"><input type="checkbox" checked={confB} onChange={e => setConfB(e.target.checked)} /><span>Revisé el estado de cuenta: son operaciones distintas</span></label>
        </div>
      )}
      {falta && !error && <div className="pa-gris" style={{ marginTop: 10 }}>{falta}</div>}
      {error && <div className="pa-note bad" role="alert">{error}</div>}
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Validar o rechazar un pago (bandeja)
// ════════════════════════════════════════════════════════════════════════
function ModalValidarPago({ api, pago, onClose, onHecho, showToast }) {
  const p = pago || {};
  const [val, setVal] = useState(null);
  const [dup, setDup] = useState(null);
  const [confDup, setConfDup] = useState(false);
  // 22-oct-2026: duplicado REAL (mismo folio de banco que otro pago): de la lectura o del 409 DUPLICADO_BANCO.
  const [dupBanco, setDupBanco] = useState(() => (p.duplicado && p.duplicado.nivel === 'real' ? lista(p.duplicado.con) : null));
  const [confBanco, setConfBanco] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const [hecho, setHecho] = useState(null);
  const [rechazo, setRechazo] = useState(null);   // {motivo, otro}
  // 24-oct-2026: la lectura dice que el dinero fue a la CLABE de la paquetería (Skydropx): no es anticipo. Validarlo pide
  // marcar que el dinero SÍ entró a la cuenta de la tienda (por si la lectura se equivocó).
  // Revisión 24-oct: si la lectura llegó DESPUÉS de abrir la bandeja, el servidor contesta 409 PAGO_PAQUETERIA y aquí se
  // enciende igual la nota (con "cerrar sin caja" y la casilla) sin tener que cerrar y volver a abrir.
  const [paqSrv, setPaqSrv] = useState(false);
  const esPaq = esPagoPaq(p) || paqSrv;
  const [confNoPaq, setConfNoPaq] = useState(false);
  const validar = async () => {
    if (!val || !val.listo || enviando) return;
    if (esPaq && !confNoPaq) { setError('Este comprobante es un pago a la paquetería: ciérralo con "Es pago a la paquetería", o marca que el dinero sí entró a la cuenta de la tienda.'); return; }
    if (dupBanco && dupBanco.length && !confBanco) { setError('Marca "Revisé el estado de cuenta: son operaciones distintas", o recházalo como Duplicado.'); return; }
    if (dup && !confDup) { setError('Marca "Sí es otro pago" si de verdad es un pago distinto.'); return; }
    setEnviando(true); setError('');
    try {
      const r = await api(BASE + '/pago/' + encodeURIComponent(p.id) + '/validar', { method: 'POST', body: { version: p.version, ...val.datos,
        ...(confDup ? { confirmar_duplicado: true } : {}), ...(confBanco ? { confirmar_duplicado_banco: true } : {}),
        ...(esPaq && confNoPaq ? { confirmar_no_paqueteria: true } : {}) } });
      setHecho(r || {});
      if (showToast) showToast('Validado ' + codAB(p.codigo));
    } catch (e) {
      const cod = errCod(e);
      if (cod === 'DUPLICADO_BANCO') { setDupBanco(lista(e.data.duplicados)); setConfBanco(false); setError('El mismo folio de banco ya está en otro pago. Revisa la lista.'); }
      else if (cod === 'DUPLICADO_POSIBLE') { setDup(lista(e.data.posibles)); setError('Parece un pago que ya se validó. Revisa la lista.'); }
      else if (cod === 'VERSION' || cod === 'ESTADO') setError('Alguien más ya movió este pago. Cierra y vuelve a abrir la bandeja.');
      else if (cod === 'SUMA_NO_CUADRA') setError('El reparto no cuadra' + (e.data.falta != null ? ' (faltan ' + $p(e.data.falta) + ')' : '') + '.');
      else if (cod === 'PAGO_PAQUETERIA') { setPaqSrv(true); setConfNoPaq(false); setError(errTxt(e)); }
      else setError(errTxt(e));
    } finally { setEnviando(false); }
  };
  // motivoDirecto: "Es pago a la paquetería" cierra sin pasar por el formulario del rechazo.
  const rechazar = async (motivoDirecto) => {
    if ((!rechazo && !motivoDirecto) || enviando) return;
    const elegido = motivoDirecto || (rechazo.motivo === 'Otro' ? (rechazo.otro || '').trim() : rechazo.motivo);
    const motivo = elegido === TXT_PAQ ? MOTIVO_PAQ : elegido;
    if (!motivo) { setError('Escribe el motivo del rechazo.'); return; }
    // Revisión 24-oct: cerrar como pago a la paquetería quita el cobro del envío del ticket del folio. Si la lectura NO
    // dice que fue la cuenta de la paquetería, se pregunta antes (que un dedo en la lista no le quite la línea "Envío").
    if (motivo === MOTIVO_PAQ && !esPaq && !window.confirm('La lectura del comprobante NO dice que el dinero fue a la cuenta de la paquetería (Skydropx). ' +
      '¿Segura que este pago es el envío pagado directo a la paquetería? Se cierra sin caja y el ticket de ' + (p.folio_sugerido || 'ese folio') + ' ya no lleva la línea "Envío".')) return;
    setEnviando(true); setError('');
    try {
      const r = await api(BASE + '/pago/' + encodeURIComponent(p.id) + '/rechazar', { method: 'POST', body: { version: p.version, motivo } });
      if (motivo === MOTIVO_PAQ) {
        const en = r && r.envio;
        if (showToast) showToast('Cerrado sin caja: ' + codAB(p.codigo) + ' es pago a la paquetería' + (en && en.anotado ? ' (anotado en el envío del pedido)' : ''));
        if (en && en.aviso) alert(en.aviso);
      } else if (showToast) showToast('Rechazado ' + codAB(p.codigo));
      onHecho && onHecho();
    } catch (e) {
      const cod = errCod(e);
      setError(cod === 'EFECTIVO_YA_COBRADO' ? 'La cajera ya cobró ese efectivo: ya no se puede rechazar. Revísalo con caja.' : errTxt(e));
    } finally { setEnviando(false); }
  };
  if (hecho) {
    return (
      <Modal titulo={'Validado ' + codAB(p.codigo)} sub={cliNom(p)} onClose={onHecho} datos="validado"
        pie={<button type="button" className="pa-btn" onClick={onHecho}>Listo</button>}>
        <div className="pa-note ok" role="status">{hecho.mensaje || 'Validado.'}</div>
        {lista(hecho.asignaciones).map(a => (
          <div key={a.id || a.codigo} className="pa-sub">{codAN(a.codigo)} · {a.folio} · {$p(a.monto)} · {a.registro && a.registro.estado ? a.registro.estado.replace(/_/g, ' ') : String(a.estado || '').replace(/_/g, ' ')}{a.registro && a.registro.ticket ? ' · ' + a.registro.ticket : ''}{a.registro && a.registro.motivo ? ' · ' + a.registro.motivo : ''}</div>
        ))}
      </Modal>
    );
  }
  const cands = lista(p.folios_candidatos).filter(f => f !== p.folio_sugerido);
  // Modo B (decisión final del dueño, 19-oct): el efectivo ya tiene su ticket ANTICIPO en caja desde
  // que se reportó y el cobro de la cajera ES la validación. Aquí solo se puede rechazar (si la clienta
  // no pagó); validarlo a mano crearía un segundo ticket por el mismo billete.
  const efectivoEnCaja = p.forma_reportada === 'efectivo' && efectivoAlReportar();
  // Prellenado con lo LEÍDO del comprobante (22-oct-2026): solo lo que la clienta/vendedora no reportó. María confirma.
  // Revisión 22-oct: con lectura dudosa (confianza baja, ilegible u operación no exitosa) no se prellena nada; con
  // tarjeta la autorización la escribe María (es su comprobación, como hoy).
  const l = lectPrellena(lectOk(p));
  const montoLeido = p.monto_reportado == null && l && l.importe != null;
  const formaLeida = (!p.forma_reportada || p.forma_reportada === 'no_se') && l && l.forma_sugerida ? l.forma_sugerida : null;
  const fechaL = fechaLeida(l);
  const refLeida = !p.referencia_reportada && l && (formaLeida || p.forma_reportada) !== 'tarjeta' ? (l.clave_rastreo || l.folio || l.autorizacion || '') : '';
  const bloqueaBanco = !!(dupBanco && dupBanco.length && !confBanco);
  const chips = chipsLectura(p, true).filter(([c, t]) => !/^DUPLICADO de/.test(t));
  return (
    <Modal titulo={'Validar ' + codAB(p.codigo) + ' · ' + (p.folio_sugerido || '')} sub={cliNom(p)} onClose={onClose} datos="validar"
      pie={rechazo
        ? <><button type="button" className="pa-btn ghost" onClick={() => { setRechazo(null); setError(''); }}>Volver</button><button type="button" className="pa-btn bad" disabled={enviando} onClick={() => rechazar()}>Confirmar rechazo</button></>
        : <><button type="button" className="pa-btn bad" onClick={() => { setRechazo({ motivo: dupBanco && dupBanco.length ? 'Duplicado' : (esPaq ? TXT_PAQ : MOTIVOS_RECHAZO[0]), otro: '' }); setError(''); }}>Rechazar</button>
          <button type="button" className="pa-btn ok" disabled={efectivoEnCaja || !val || !val.listo || enviando || bloqueaBanco || (esPaq && !confNoPaq)} onClick={validar}>{enviando ? 'Validando…' : 'Validar'}</button></>}>
      {efectivoEnCaja && <div className="pa-note warn" style={{ marginTop: 0, marginBottom: 10 }}>Efectivo: la cajera ya tiene el ticket ANTICIPO por {$p(p.monto_reportado)}; cuando lo cobre queda validado solo. Recházalo solo si la clienta no va a pagar.</div>}
      {esPaq && !rechazo && (
        <div className="pa-note" data-pa="pago-paqueteria" style={{ marginTop: 0, marginBottom: 10, color: 'var(--ap)', background: 'var(--apb)' }}>
          <b>Pago a la paquetería (Skydropx), no es anticipo.</b> El comprobante dice que el dinero fue a la cuenta de la paquetería: la clienta pagó su ENVÍO directo y eso se vuelve saldo para la guía. No entra al banco de la tienda ni a la caja.
          <div style={{ marginTop: 8 }}>
            <button type="button" className="pa-btn full" data-pa="cerrar-paqueteria" disabled={enviando} onClick={() => rechazar(TXT_PAQ)}>{enviando ? 'Cerrando…' : 'Es pago a la paquetería: cerrar sin caja'}</button>
          </div>
          <div className="pa-gris" style={{ marginTop: 6 }}>Queda en el historial como “Pago a paquetería” y se anota en el envío del pedido de {p.folio_sugerido || 'este folio'} (su ticket no lleva la línea “Envío”).</div>
          <label className="pa-ck"><input type="checkbox" checked={confNoPaq} onChange={e => setConfNoPaq(e.target.checked)} /><span>Revisé el estado de cuenta: el dinero SÍ entró a la cuenta de la tienda (la lectura se equivocó)</span></label>
        </div>
      )}
      {dupBanco && dupBanco.length > 0 && (
        <div className="pa-note bad" data-pa="duplicado-banco" style={{ marginTop: 0, marginBottom: 10 }}>
          <b>Duplicado: mismo folio de banco que {dupBanco.map(o => codAB(o.codigo)).join(', ')}.</b> El dinero de una sola operación no se valida dos veces.
          <pre style={{ whiteSpace: 'pre-wrap', margin: '6px 0', font: 'inherit' }}>{textoDupBanco(dupBanco)}</pre>
          Si una transferencia pagó dos apartados, valida UNO repartido entre los folios y rechaza el otro como Duplicado.
          {!rechazo && <label className="pa-ck"><input type="checkbox" checked={confBanco} onChange={e => setConfBanco(e.target.checked)} /><span>Revisé el estado de cuenta: son operaciones distintas</span></label>}
        </div>
      )}
      <VerComprobante archivoId={p.archivo_id} />
      <div className="pa-sub" style={{ marginTop: 8 }}>
        Reportó {p.reportado_por || '—'} ({NOM_CANAL[p.canal] || p.canal || '—'}) {haceTxt(p.reportado_at)} · {p.monto_reportado != null ? $p(p.monto_reportado) : 'sin monto'} · {nomForma(p.forma_reportada)}
        {p.referencia_reportada ? ' · ref ' + p.referencia_reportada : ''}{cands.length ? ' · otros apartados: ' + cands.join(', ') : ''}
      </div>
      {chips.length > 0 && <div className="pa-chips" data-pa="chips-lectura">{chips.map(([c, t], i) => <span key={i} className={c}>{t}</span>)}</div>}
      <BloqueLectura api={api} pago={p} showToast={showToast} />
      {p.nota && <div className="pa-note">Nota: {p.nota}</div>}
      {(p.posible_reuso_de || p.posible_reuso) && <div className="pa-note bad">Posible reuso: ese mismo archivo ya se mandó en {(p.posible_reuso && p.posible_reuso.folio) || 'otro folio'} {p.posible_reuso && p.posible_reuso.codigo ? '(' + codAB(p.posible_reuso.codigo) + ')' : ''}.</div>}
      {p.resolver_tras_entrega && <div className="pa-note warn">Al entregar se cobró completo ({p.resolver_tras_entrega_ref || 'ticket de entrega'}): al validarlo quedará en Por resolver.</div>}
      {rechazo ? (
        <div data-pa="rechazo">
          <span className="pa-lbl">Motivo del rechazo</span>
          <div className="pa-seg" role="group" aria-label="Motivo del rechazo">
            {MOTIVOS_RECHAZO.map(m => <button key={m} type="button" className={rechazo.motivo === m ? 'on' : ''} aria-pressed={rechazo.motivo === m} onClick={() => setRechazo({ ...rechazo, motivo: m })}>{m}</button>)}
          </div>
          {rechazo.motivo === 'Otro' && <input className="pa-in" style={{ marginTop: 8 }} aria-label="Otro motivo" value={rechazo.otro} onChange={e => setRechazo({ ...rechazo, otro: e.target.value })} placeholder="¿Por qué se rechaza?" />}
          <div className="pa-note">A la clienta NO se le avisa nada; quien lo reportó recibe el aviso.</div>
        </div>
      ) : (
        <FormValidacion api={api} partnerId={p.partner_id} folioSugerido={p.folio_sugerido} candidatos={p.folios_candidatos}
          monto0={p.monto_reportado != null ? String(p.monto_reportado) : (montoLeido ? String(l.importe) : '')}
          forma0={formaLeida || p.forma_reportada} ref0={p.referencia_reportada || refLeida} fecha0={fechaL}
          leido={{ monto: montoLeido, forma: !!formaLeida, fecha: !!fechaL, ref: !!refLeida }} conVisto onCambio={setVal} />
      )}
      {dup && !rechazo && (
        <div className="pa-note bad" data-pa="duplicado">
          Posible duplicado de:
          <pre style={{ whiteSpace: 'pre-wrap', margin: '6px 0', font: 'inherit' }}>{textoDuplicados(dup)}</pre>
          <label className="pa-ck"><input type="checkbox" checked={confDup} onChange={e => setConfDup(e.target.checked)} /><span>Sí es otro pago</span></label>
        </div>
      )}
      {!rechazo && val && !val.listo && !error && <div className="pa-gris" style={{ marginTop: 10 }}>{val.motivo}</div>}
      {error && <div className="pa-note bad" role="alert">{error}</div>}
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Resolver / decidir / traspasar el dinero de una asignación
// ════════════════════════════════════════════════════════════════════════
const ACCIONES = {
  a_folio: 'Pasar a un apartado vivo', saldo_a_favor: 'Dejar a favor de la clienta', devolver: 'Devolver', ya_cobrado: 'Ya se cobró en caja',
  dejar_favor: 'Dejar a favor de la clienta', devolver_dec: 'Devolver', traspasar: 'Traspasar a otro apartado', cancelar_devolucion: 'Cancelar la devolución',
};
// 20-oct-2026: el buscador de Tickets de Odoo 19 no busca por el nombre de la línea: se busca por la clienta
// (o por el recibo ANT-AN…, que solo llevan los anticipos que no son efectivo).
const instruccionDevolucion = (a, R, forma, nombre) => 'Punto de venta → Tickets → pestaña Pagados → busca por Cliente: ' + (nombre || 'la clienta de esta tarjeta') +
  (a.forma !== 'efectivo' && a.codigo ? ' (o por Número de recibo ANT-' + String(codAN(a.codigo)).replace('-', '') + ')' : '') +
  ' → abre el ticket ANTICIPO de ' + $p(a.monto_registrado || a.monto) + ' (línea «ANTICIPO APARTADO ' + (a.folio_origen || a.folio) + ' · ' + codAN(a.codigo) + '»)' +
  ' → Reembolsar → ' + (cents(R) < cents(a.monto_registrado || a.monto) ? 'cambia el precio de la línea a ' + $p(R) + ' → ' : '') +
  'cobra el reembolso con ' + nomForma(forma || a.devolucion_forma || a.forma) + '. Después sube aquí la ' + (a.devolucion_motivo === 'reverso_bancario' ? 'foto del estado de cuenta.' : 'foto de la devolución.');

function TicketEntrega({ t }) {
  if (!t) return null;
  // 20-oct-2026: si Odoo no contestó, el servidor manda {error}; antes se pintaba "sin pagos" y María decidía
  // creyendo que en caja no se cobró nada.
  if (t.error) return (
    <div className="pa-note warn" role="alert" data-pa="ticket-entrega-error">
      No pude leer el ticket de entrega en Odoo ({t.error}). Revísalo en el POS antes de decidir.
    </div>
  );
  return (
    <div className="pa-note" data-pa="ticket-entrega">
      Ticket de entrega {t.name || '—'} ({t.state || '—'}): {lista(t.pagos).length ? lista(t.pagos).map(x => metodoDe(x) + ' ' + $p(x.monto != null ? x.monto : x.amount)).join(' · ') : 'sin pagos'}
    </div>
  );
}

function ModalAccion({ api, item, accion, onClose, onHecho, showToast }) {
  const a = item || {};
  const resolver = a.estado === 'por_resolver';
  const [nota, setNota] = useState('');
  const [destino, setDestino] = useState('');
  const [folios, setFolios] = useState(null);
  const [motivoDev, setMotivoDev] = useState('cliente');
  const [formaDev, setFormaDev] = useState(a.forma || 'transferencia');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const [hecho, setHecho] = useState(null);
  const necesitaFolio = accion === 'a_folio' || accion === 'traspasar';
  useEffect(() => {
    if (!necesitaFolio) return undefined;
    let vivo = true;
    api(BASE + '/folios-vivos?partner_id=' + encodeURIComponent(a.partner_id || ''))
      .then(r => { if (vivo) setFolios(lista(r && r.folios).filter(f => f.folio !== a.folio)); })
      .catch(e => { if (vivo) { setFolios([]); setError(errTxt(e)); } });
    return () => { vivo = false; };
    /* eslint-disable-next-line */
  }, [accion]);
  const dispo = a.disponible != null ? a.disponible : (a.monto_registrado != null ? a.monto_registrado : a.monto);
  let falta = '';
  if (necesitaFolio && !destino) falta = 'Elige el apartado.';
  else if (resolver && !nota.trim()) falta = 'Escribe una nota (queda en el historial).';
  const hacer = async () => {
    if (falta || enviando) return;
    setEnviando(true); setError('');
    try {
      let r;
      if (resolver) {
        const decision = accion === 'devolver_dec' ? 'devolver' : accion === 'dejar_favor' ? 'saldo_a_favor' : accion;
        r = await api(BASE + '/resolver', { method: 'POST', body: { asignacion_ids: [a.id], decision, ...(destino ? { folio_destino: destino } : {}), nota: nota.trim() } });
      } else if (accion === 'traspasar') {
        r = await api(BASE + '/traspasar', { method: 'POST', body: { asignacion_id: a.id, version: a.version, folio_destino: destino } });
      } else {
        const decision = accion === 'devolver_dec' || accion === 'devolver' ? 'devolver' : accion === 'cancelar_devolucion' ? 'cancelar_devolucion' : 'saldo_a_favor';
        const body = { version: a.version, decision };
        if (decision === 'devolver') { body.devolucion_motivo = motivoDev; body.devolucion_forma = formaDev; }
        r = await api(BASE + '/asignacion/' + encodeURIComponent(a.id) + '/decidir', { method: 'POST', body });
      }
      if (showToast) showToast('Listo · ' + codAN(a.codigo));
      if (r && (r.instruccion || (r.devolver && r.devolver.instruccion))) setHecho(r);
      else if (onHecho) onHecho();
    } catch (e) {
      const cod = errCod(e);
      setError(cod === 'VERSION' || cod === 'ESTADO' ? 'Alguien más ya movió este dinero. Cierra y vuelve a abrir la bandeja.'
        : cod === 'SIN_DISPONIBLE' ? 'Ya no tiene dinero disponible (¿se aplicó en una entrega?).' : errTxt(e));
    } finally { setEnviando(false); }
  };
  if (hecho) {
    const ins = hecho.instruccion || (hecho.devolver && hecho.devolver.instruccion);
    return (
      <Modal titulo="Devolución pedida" sub={codAN(a.codigo) + ' · ' + (a.folio || '')} onClose={onHecho} datos="accion-hecha"
        pie={<button type="button" className="pa-btn" onClick={onHecho}>Listo</button>}>
        <div className="pa-note warn" role="status">{ins}</div>
      </Modal>
    );
  }
  const esDevolver = accion === 'devolver_dec' || (accion === 'devolver' && !resolver);
  return (
    <Modal titulo={ACCIONES[accion] || accion} sub={codAN(a.codigo) + ' · ' + (a.folio || '') + ' · ' + cliNom(a)} onClose={onClose} datos="accion"
      pie={<><button type="button" className="pa-btn ghost" onClick={onClose}>Cancelar</button><button type="button" className="pa-btn" disabled={!!falta || enviando} onClick={hacer}>{enviando ? '…' : 'Confirmar'}</button></>}>
      <div className="pa-sub">Disponible {$p(dispo)} · {nomForma(a.forma)}{a.motivo && MOTIVO_RESOLVER[a.motivo] ? ' · ' + MOTIVO_RESOLVER[a.motivo] : ''}</div>
      {accion === 'ya_cobrado' && <><TicketEntrega t={a.ticket_entrega} /><div className="pa-note warn">Confirmas que este dinero YA entró en el ticket de entrega: no se registra otra vez en caja. Es final.</div></>}
      {(accion === 'saldo_a_favor' || accion === 'dejar_favor') && <div className="pa-note">El dinero queda a favor de la clienta (ya no es de un folio): se le ofrece al cobrar cualquier pedido suyo y el apartado ya se puede borrar.</div>}
      {accion === 'devolver' && resolver && <div className="pa-note warn">Se registra en caja y luego pasa a Por devolver: la cajera lo reembolsa con el monto exacto que diga la bandeja.</div>}
      {accion === 'cancelar_devolucion' && <div className="pa-note">La devolución se cancela y el dinero vuelve a estar disponible (solo si la cajera todavía no reembolsó).</div>}
      {esDevolver && (
        <>
          <span className="pa-lbl">¿Por qué se devuelve?</span>
          <div className="pa-seg" role="group" aria-label="Motivo de la devolución">
            <button type="button" className={motivoDev === 'cliente' ? 'on' : ''} onClick={() => setMotivoDev('cliente')}>La clienta lo pide</button>
            <button type="button" className={motivoDev === 'reverso_bancario' ? 'on' : ''} onClick={() => setMotivoDev('reverso_bancario')}>Reverso del banco</button>
          </div>
          <span className="pa-lbl">¿Cómo se le devuelve?</span>
          <div className="pa-seg" role="group" aria-label="Forma de la devolución">
            {FORMAS_VAL.map(([v, t]) => <button key={v} type="button" className={formaDev === v ? 'on' : ''} onClick={() => setFormaDev(v)}>{t}</button>)}
          </div>
          {motivoDev === 'reverso_bancario' && <div className="pa-note">Reverso bancario: la cajera reembolsa con el método de origen (no sale dinero físico) y la evidencia es el estado de cuenta.</div>}
        </>
      )}
      {necesitaFolio && (
        <>
          <label className="pa-lbl" htmlFor="pa-a-folio">Apartado vivo de la misma clienta</label>
          {!folios ? <div className="pa-gris">Cargando…</div> : folios.length === 0 ? <div className="pa-note warn">La clienta no tiene otro apartado vivo.</div> : (
            <select id="pa-a-folio" className="pa-in" value={destino} onChange={e => setDestino(e.target.value)}>
              <option value="">Elige…</option>
              {folios.map(f => <option key={f.folio} value={f.folio}>{f.folio} · {$p(f.total_mxn)}{f.resumen ? ' · resta ' + $p(f.resumen.resta) : ''}</option>)}
            </select>
          )}
        </>
      )}
      {resolver && (<><label className="pa-lbl" htmlFor="pa-a-nota">Nota (obligatoria)</label><textarea id="pa-a-nota" className="pa-in" rows={2} value={nota} onChange={e => setNota(e.target.value)} /></>)}
      {falta && !error && <div className="pa-gris" style={{ marginTop: 10 }}>{falta}</div>}
      {error && <div className="pa-note bad" role="alert">{error}</div>}
    </Modal>
  );
}

// Detalle de un pago del historial (y "deshacer validación" si nada tocó Odoo)
function ModalDetallePago({ api, pago, onClose, onHecho, showToast }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);
  useEffect(() => {
    let vivo = true;
    api(BASE + '/pago/' + encodeURIComponent(pago.id)).then(r => { if (vivo) setD(r || {}); }).catch(e => { if (vivo) setError(errTxt(e)); });
    return () => { vivo = false; };
    /* eslint-disable-next-line */
  }, [pago.id]);
  const p = (d && d.pago) || pago;
  const deshacer = async () => {
    const motivo = window.prompt('¿Por qué se deshace la validación de ' + codAB(p.codigo) + '? (obligatorio)');
    if (!motivo || !motivo.trim()) return;
    setEnviando(true); setError('');
    try {
      await api(BASE + '/pago/' + encodeURIComponent(p.id) + '/deshacer', { method: 'POST', body: { version: p.version, motivo: motivo.trim() } });
      if (showToast) showToast('Validación deshecha · ' + codAB(p.codigo));
      onHecho && onHecho();
    } catch (e) { setError(errCod(e) === 'ESTADO' ? 'Ya no se puede deshacer: algo de este pago ya entró a caja.' : errTxt(e)); }
    finally { setEnviando(false); }
  };
  return (
    <Modal titulo={codAB(p.codigo) + ' · ' + (p.folio_sugerido || '')} sub={cliNom(p)} onClose={onClose} datos="detalle"
      pie={p.estado === 'validado' ? <><button type="button" className="pa-btn ghost" onClick={onClose}>Cerrar</button><button type="button" className="pa-btn bad" disabled={enviando} onClick={deshacer}>Deshacer validación</button></> : <button type="button" className="pa-btn" onClick={onClose}>Cerrar</button>}>
      <VerComprobante archivoId={p.archivo_id} />
      <div className="pa-sub" style={{ marginTop: 8 }}>{String(p.estado || '').replace(/_/g, ' ')} · {$p(p.monto_real != null ? p.monto_real : p.monto_reportado)} · {nomForma(p.forma || p.forma_reportada)}{p.fecha_deposito ? ' · depósito ' + p.fecha_deposito : ''}{p.validado_por ? ' · validó ' + p.validado_por : ''}{p.autovalidado ? ' · AUTOVALIDADO' : ''}{p.motivo_rechazo ? ' · ' + motivoTxt(p.motivo_rechazo) : ''}</div>
      {chipsLectura(p, true).length > 0 && <div className="pa-chips" data-pa="chips-lectura">{chipsLectura(p, true).map(([c, t], i) => <span key={i} className={c}>{t}</span>)}</div>}
      <BloqueLectura api={api} pago={p} showToast={showToast} />
      {!d && !error && <div className="pa-gris" style={{ marginTop: 8 }}>Cargando…</div>}
      {d && lista(d.asignaciones).map(a => <div key={a.id} className="pa-sub">{codAN(a.codigo)} · {a.folio} · {$p(a.monto)} · {String(a.estado || '').replace(/_/g, ' ')}{a.ticket_name ? ' · ' + a.ticket_name : ''}</div>)}
      {d && lista(d.eventos).length > 0 && <div className="pa-note">{lista(d.eventos).slice(-12).map((ev, i) => <div key={i}>{fechaMx(ev.at)} {horaMx(ev.at)} · {ev.accion} · {ev.por}</div>)}</div>}
      {error && <div className="pa-note bad" role="alert">{error}</div>}
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Bandeja de quien valida (María)
// ════════════════════════════════════════════════════════════════════════
const VISTAS = [['por_validar', 'Por validar'], ['en_caja', 'En caja'], ['por_resolver', 'Por resolver'], ['vencidos', 'Vencidos con dinero'], ['saldos', 'Saldos a favor'], ['alertas', 'Alertas'], ['historial', 'Historial']];
const cuentaVista = (c, v) => {
  if (!c) return 0;
  if (v === 'por_validar') return c.por_validar_n || 0;
  if (v === 'en_caja') return (c.en_cola_n || 0) + (c.efectivo_sin_cobrar_n || 0) + (c.por_devolver_n || 0);
  if (v === 'por_resolver') return (c.por_resolver_n || 0) + (c.por_decidir_n || 0);
  if (v === 'vencidos') return c.vencidos_n || 0;
  if (v === 'alertas') return c.alertas_dinero_n || 0;
  return 0;
};
const esEntrega = (it) => it && (it.tipo === 'entrega' || it.tipo === 'entrega_cancelada' || (it.total_prendas != null && it.anticipo != null && !it.pago_id));

export function BandejaPagosAp({ api, showToast, onCambio, onClose }) {
  ponerCSS();
  const [vista, setVista] = useState('por_validar');
  const [cont, setCont] = useState(_cont);
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [desde, setDesde] = useState(restaDias(hoyMx(), 7));
  const [hasta, setHasta] = useState(hoyMx());
  const [ocupado, setOcupado] = useState('');
  const seq = useRef(0);
  const fechas = useRef({ desde, hasta });
  fechas.current = { desde, hasta };
  const cargar = useCallback(async (v) => {
    const n = ++seq.current;
    setItems(null); setError('');
    const q = v === 'historial' ? '&desde=' + fechas.current.desde + '&hasta=' + fechas.current.hasta : '';
    try {
      const r = await api(BASE + '/bandeja?vista=' + v + q);
      if (n !== seq.current) return;
      let its = lista(r && r.items);
      if (v === 'por_validar') its = its.slice().sort((x, y) => String(x.reportado_at || '').localeCompare(String(y.reportado_at || '')));
      if (v === 'alertas') its = its.slice().sort((x, y) => (x.gravedad === 'dinero' ? 0 : 1) - (y.gravedad === 'dinero' ? 0 : 1));
      if (v === 'historial') {
        const { desde: d1, hasta: d2 } = fechas.current;
        its = its.filter(p => { const f = fechaMx(p.validado_at || p.updated_at || p.reportado_at); return !f || (f >= d1 && f <= d2); });
      }
      setItems(its);
    } catch (e) { if (n === seq.current) { setError(errTxt(e)); setItems([]); } }
  }, [api]);
  useEffect(() => { cargar(vista); }, [vista, cargar]);
  useEffect(() => { cargarContadorPagosAp(api).then(setCont).catch(() => {}); }, [api]);
  const recargar = () => {
    setModal(null);
    cargar(vista);
    cargarContadorPagosAp(api).then(setCont).catch(() => {});
    if (onCambio) onCambio();
  };
  const registrarAhora = async (a) => {
    setOcupado(a.id);
    try {
      const r = await api(BASE + '/asignacion/' + encodeURIComponent(a.id) + '/registrar-ahora', { method: 'POST', body: {} });
      const g = (r && r.registro) || {};
      showToast && showToast(g.estado === 'registrado' ? '✅ Registrado en caja ' + (g.ticket || '') : 'Sigue en cola: ' + String(g.motivo || g.estado || 'sin respuesta').replace(/_/g, ' '));
      recargar();
    } catch (e) { alert(errTxt(e)); }
    finally { setOcupado(''); }
  };
  // 20-oct-2026 (revisión "tienda"): el efectivo en modo B nace como borrador en caja y sale de "Por validar";
  // sin este botón María no podía rechazarlo si la clienta no llegó (y el borrador impide cerrar la caja).
  // /rechazar ya revisa en Odoo que la cajera no lo haya cobrado y cancela su propio borrador.
  const rechazarEfectivo = async (a) => {
    const motivo = window.prompt('¿Por qué se rechaza? (ej. La clienta no dejó el efectivo / monto mal capturado)');
    if (!motivo || !motivo.trim()) return;
    setOcupado(a.id);
    try {
      await api(BASE + '/pago/' + encodeURIComponent(a.pago.id) + '/rechazar', { method: 'POST', body: { version: a.pago.version, motivo: motivo.trim() } });
      showToast && showToast('Rechazado · ' + codAB(a.pago.codigo));
      recargar();
    } catch (e) {
      const c = errCod(e);
      alert(c === 'EFECTIVO_YA_COBRADO' ? 'La cajera ya cobró ese efectivo: ya no se puede rechazar. Revísalo con caja.'
        : (c === 'VERSION' || c === 'ESTADO') ? 'Alguien más lo movió: actualiza la bandeja.' : errTxt(e));
    } finally { setOcupado(''); }
  };
  const subirEvidencia = async (a, file) => {
    if (!file) return;
    setOcupado(a.id);
    try {
      const z = await comprimirComprobante(file);
      await api(BASE + '/asignacion/' + encodeURIComponent(a.id) + '/evidencia', { method: 'POST', body: { archivo: { b64: z.b64, mime: z.mime, ...(z.mini_b64 ? { mini_b64: z.mini_b64 } : {}), sha256_original: z.sha256_original } } });
      showToast && showToast('Evidencia guardada · ' + codAN(a.codigo));
      recargar();
    } catch (e) { alert(errTxt(e)); }
    finally { setOcupado(''); }
  };
  const cerrarAlerta = async (al) => {
    const nota = window.prompt('¿Qué se revisó? (la nota es obligatoria)');
    if (!nota || !nota.trim()) return;
    setOcupado('al' + al.id);
    try { await api(BASE + '/alerta/' + encodeURIComponent(al.id) + '/cerrar', { method: 'POST', body: { nota: nota.trim() } }); recargar(); }
    catch (e) { alert(errTxt(e)); }
    finally { setOcupado(''); }
  };
  const reenviar = async (en) => {
    setOcupado('en' + en.id);
    try { if (await reenviarEntrega(api, en, showToast)) recargar(); }
    finally { setOcupado(''); }
  };
  const acc = (it, accion, txt, clase) => <button key={accion} type="button" className={'pa-btn sm ' + (clase || 'ghost')} onClick={() => setModal({ tipo: 'accion', item: it, accion })}>{txt}</button>;

  const tarjetaPago = (p, enHistorial) => {
    // 22-oct-2026: lo leído del comprobante distingue dos pagos de la misma clienta (caso WA-1184: dos transferencias
    // BBVA distintas que salían iguales, "sin monto"). Sin lectura, la tarjeta queda como antes.
    const l = lectOk(p);
    const chipsL = chipsLectura(p);
    const leidoMonto = !(p.monto_real != null && enHistorial) && p.monto_reportado == null && l && l.importe != null;
    return (
    <div key={p.id} className={'pa-item' + (p.duplicado && p.duplicado.nivel === 'real' ? ' din' : '')} data-pa="pago" data-id={p.id}>
      <div className="pa-item-h">
        <button type="button" className="pa-mini" aria-label="Ver comprobante" onClick={() => setModal({ tipo: enHistorial ? 'detalle' : 'validar', pago: p })}>
          {miniDe(p) ? <ImgAuth url={miniDe(p)} alt="Comprobante" /> : p.archivo_id ? 'PDF' : 'sin archivo'}
        </button>
        <div className="tx">
          <b>{cliNom(p)}</b>
          <div className="pa-sub">{codAB(p.codigo)} · {p.folio_sugerido}{lista(p.folios_candidatos).filter(f => f !== p.folio_sugerido).length ? ' (también ' + lista(p.folios_candidatos).filter(f => f !== p.folio_sugerido).join(', ') + ')' : ''}</div>
          <div className="pa-sub">{nomForma(p.forma_reportada)} · {p.reportado_por || '—'} · {NOM_CANAL[p.canal] || p.canal || ''} · {haceTxt(p.reportado_at)}</div>
          {l && <div className="pa-sub" data-pa="lectura" style={{ color: 'var(--ink)' }}><LineaLectura l={l} /></div>}
          {enHistorial && <div className="pa-sub">{p.estado === 'rechazado' ? (p.motivo_rechazo === MOTIVO_PAQ ? motivoTxt(p.motivo_rechazo) : 'RECHAZADO: ' + (p.motivo_rechazo || '')) : 'VALIDADO por ' + (p.validado_por || '—') + (p.autovalidado ? ' · AUTOVALIDADO' : '')}</div>}
        </div>
        <span className="m">{p.monto_real != null && enHistorial ? $p(p.monto_real) : p.monto_reportado != null ? $p(p.monto_reportado) : leidoMonto ? <>{$p(l.importe)}<small className="pa-gris" style={{ display: 'block', textAlign: 'right', fontWeight: 400 }}>leído</small></> : 'sin monto'}</span>
      </div>
      {(p.posible_reuso_de || p.posible_reuso || p.posible_duplicado || p.resolver_tras_entrega || (enHistorial && p.autovalidado) || chipsL.length > 0) && (
        <div className="pa-chips" data-pa="chips">
          {chipsL.filter(([c]) => c === 'bad').map(([c, t], i) => <span key={'lb' + i} className={c}>{t}</span>)}
          {(p.posible_reuso_de || p.posible_reuso) && <span className="bad">Posible reuso{p.posible_reuso && p.posible_reuso.codigo ? ' de ' + codAB(p.posible_reuso.codigo) : ''}</span>}
          {p.posible_duplicado && <span className="bad">Posible duplicado{p.duplicado && p.duplicado.nivel === 'posible' ? ' de ' + conDup(p.duplicado).join(', ') + porQueDup(p.duplicado) : ''}</span>}
          {chipsL.filter(([c]) => c !== 'bad').map(([c, t], i) => <span key={'lo' + i} className={c}>{t}</span>)}
          {p.resolver_tras_entrega && <span className="warn">Se cobró completo al entregar</span>}
          {enHistorial && p.autovalidado && <span className="warn">Autovalidado</span>}
        </div>
      )}
      <div className="pa-acts">
        {enHistorial
          ? <button type="button" className="pa-btn sm ghost" onClick={() => setModal({ tipo: 'detalle', pago: p })}>Ver</button>
          : <button type="button" className="pa-btn sm" onClick={() => setModal({ tipo: 'validar', pago: p })}>Revisar y validar</button>}
      </div>
    </div>
    );
  };
  const encabezadoAsig = (a, extra) => (
    <div className="pa-item-h">
      <div className="tx">
        <b>{cliNom(a)}</b>
        <div className="pa-sub">{codAN(a.codigo)} · {a.folio}{a.folio_origen && a.folio_origen !== a.folio ? ' (antes ' + a.folio_origen + ')' : ''} · {nomForma(a.forma)}</div>
        {extra}
      </div>
      <span className="m">{$p(a.disponible != null && cents(a.disponible) > 0 ? a.disponible : (a.monto_registrado != null ? a.monto_registrado : a.monto))}</span>
    </div>
  );
  const tarjetaCaja = (a) => {
    const R = a.devolucion_monto != null ? a.devolucion_monto : a.disponible;
    return (
      <div key={a.id} className="pa-item" data-pa="caja" data-estado={a.estado}>
        {encabezadoAsig(a, <div className="pa-sub">{
          a.estado === 'borrador_caja' ? 'Efectivo: la cajera debe cobrarlo al recibir el billete' + (a.ticket_name ? ' (ticket ' + a.ticket_name + ')' : '') + ' · ' + haceTxt(a.registrado_at || a.updated_at || a.creado_at)
            : a.estado === 'por_devolver' ? 'POR DEVOLVER ' + $p(R) + ' (' + (a.devolucion_motivo === 'reverso_bancario' ? 'reverso bancario' : 'la clienta lo pide') + ')'
              : 'En cola de caja' + (a.ultimo_error ? ': ' + String(a.ultimo_error).replace(/_/g, ' ') : '') + ' · intentos ' + (a.intentos || 0) + (a.proximo_intento ? ' · próximo ' + horaMx(a.proximo_intento) : '')
        }</div>)}
        {a.estado === 'por_devolver' && <div className="pa-note warn" data-pa="instruccion">{nombreReal(a) ? instruccionDevolucion(a, R, a.devolucion_forma, nombreReal(a)) : (a.instruccion || instruccionDevolucion(a, R, a.devolucion_forma))}</div>}
        <div className="pa-acts">
          {(a.estado === 'en_cola' || a.estado === 'registrando') && <button type="button" className="pa-btn sm" disabled={ocupado === a.id} onClick={() => registrarAhora(a)}>{ocupado === a.id ? '…' : 'Registrar ahora'}</button>}
          {a.estado === 'borrador_caja' && a.pago && a.pago.estado === 'por_validar' && <button type="button" className="pa-btn sm bad" data-pa="rechazar-efectivo" disabled={ocupado === a.id} onClick={() => rechazarEfectivo(a)}>{ocupado === a.id ? '…' : 'Rechazar: la clienta no pagó'}</button>}
          {a.estado === 'por_devolver' && (
            <label className="pa-btn sm" style={{ cursor: 'pointer' }}>
              {ocupado === a.id ? 'Subiendo…' : a.devolucion_motivo === 'reverso_bancario' ? 'Subir estado de cuenta' : 'Subir foto de la devolución'}
              <input type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={e => { subirEvidencia(a, e.target.files && e.target.files[0]); e.target.value = ''; }} />
            </label>
          )}
          {a.estado === 'por_devolver' && acc(a, 'cancelar_devolucion', 'Cancelar devolución')}
        </div>
      </div>
    );
  };
  const tarjetaEntrega = (en) => (
    <div key={'en' + en.id} className="pa-item din" data-pa="entrega-cancelada">
      <div className="pa-item-h"><div className="tx"><b>{cliNom(en)}</b><div className="pa-sub">La cajera canceló el ticket {en.ticket_name || ''} de {en.folio || '—'} · anticipo {$p(en.anticipo)}</div></div><span className="m">{$p(en.anticipo)}</span></div>
      <div className="pa-acts"><button type="button" className="pa-btn sm" disabled={ocupado === 'en' + en.id} onClick={() => reenviar(en)}>Reenviar a caja</button></div>
    </div>
  );
  const tarjetaResolver = (a) => {
    if (esEntrega(a)) return tarjetaEntrega(a);
    if (a.estado === 'por_resolver') {
      return (
        <div key={a.id} className="pa-item" data-pa="por-resolver">
          {encabezadoAsig(a, <div className="pa-sub">{MOTIVO_RESOLVER[a.motivo] || String(a.motivo || '').replace(/_/g, ' ')}</div>)}
          <TicketEntrega t={a.ticket_entrega} />
          <div className="pa-acts">{acc(a, 'a_folio', 'Pasar a un apartado vivo')}{acc(a, 'saldo_a_favor', 'Dejar a favor')}{acc(a, 'devolver', 'Devolver')}{acc(a, 'ya_cobrado', 'Ya se cobró en caja')}</div>
        </div>
      );
    }
    const enCancel = a.entrega_cancelada || a.reenviable;
    return (
      <div key={a.id} className="pa-item" data-pa="por-decidir">
        {encabezadoAsig(a, <div className="pa-sub">El apartado ya no está vivo y el dinero sigue registrado: decide qué se hace.</div>)}
        <TicketEntrega t={a.ticket_entrega} />
        <div className="pa-acts">{acc(a, 'dejar_favor', 'Dejar a favor')}{acc(a, 'devolver_dec', 'Devolver')}{acc(a, 'traspasar', 'Traspasar')}
          {enCancel && typeof enCancel === 'object' && <button type="button" className="pa-btn sm" onClick={() => reenviar(enCancel)}>Reenviar</button>}</div>
      </div>
    );
  };
  const tarjetaVencido = (it) => {
    const r = it.resumen || {};
    const fr = it.fecha_recoleccion || r.fecha_recoleccion;
    // El item puede ser el folio (con sus asignaciones con dinero) o una asignación suelta.
    const esAsig = it.pago_id || it.tipo === 'asignacion';
    const asigs = lista(it.asignaciones).length ? it.asignaciones.filter(x => cents(x.disponible) > 0 || x.disponible == null) : (esAsig ? [it] : []);
    const dispo = it.disponible != null ? it.disponible : r.disponible;
    const dias = it.dias_vencido != null ? Number(it.dias_vencido) : diasVencido(fr);
    return (
      <div key={it.id || it.folio} className="pa-item" data-pa="vencido">
        <div className="pa-item-h"><div className="tx"><b>{cliNom(it)}</b>
          <div className="pa-sub">{it.folio} · recogía {fr ? fechaMx(fr) : '—'} · {dias} días vencido</div>
          <div className="pa-sub">Disponible {$p(dispo)} · por validar {$p(r.por_validar_monto)}</div></div></div>
        {asigs.map(a => <div key={a.id} className="pa-acts">{asigs.length > 1 && <span className="pa-sub" style={{ flexBasis: '100%' }}>{codAN(a.codigo)} · {$p(a.disponible)}</span>}{acc({ ...it, ...a, folio: a.folio || it.folio }, 'dejar_favor', 'Dejar a favor')}{acc({ ...it, ...a, folio: a.folio || it.folio }, 'devolver_dec', 'Devolver')}</div>)}
        {!asigs.length && (cents(dispo) > 0
          ? <div className="pa-note warn">Tiene {$p(dispo)} registrado, pero no llegó el detalle para decidir. Actualiza la bandeja; si sigue igual, avisa a sistemas.</div>
          : <div className="pa-note">Solo tiene pagos por validar: primero valídalos o recházalos.</div>)}
      </div>
    );
  };
  const tarjetaSaldo = (a) => (
    <div key={a.id} className="pa-item" data-pa="saldo">
      {encabezadoAsig(a, <div className="pa-sub">A favor de la clienta · de {a.folio_origen || a.folio}{a.situacion === 'por_decidir' ? ' · por decidir' : ''}</div>)}
      <div className="pa-acts">{acc(a, 'devolver_dec', 'Devolver')}{acc(a, 'traspasar', 'Traspasar')}</div>
    </div>
  );
  const tarjetaAlerta = (al) => (
    <div key={al.id} className={'pa-item' + (al.gravedad === 'dinero' ? ' din' : '')} data-pa="alerta">
      <div className="pa-item-h"><div className="tx"><b>{al.gravedad === 'dinero' ? 'DINERO · ' : al.gravedad === 'revisar' ? 'Revisar · ' : ''}{String(al.tipo || '').replace(/_/g, ' ')}</b>
        <div className="pa-sub">{al.folio || ''}{al.folio ? ' · ' : ''}{haceTxt(al.creado_at)}</div></div></div>
      <div className="pa-note">{al.texto}</div>
      <div className="pa-acts"><button type="button" className="pa-btn sm ghost" disabled={ocupado === 'al' + al.id} onClick={() => cerrarAlerta(al)}>Revisado</button></div>
    </div>
  );
  const pinta = (it) => {
    if (vista === 'por_validar') return tarjetaPago(it, false);
    if (vista === 'historial') return tarjetaPago(it, true);
    if (vista === 'en_caja') return tarjetaCaja(it);
    if (vista === 'por_resolver') return tarjetaResolver(it);
    if (vista === 'vencidos') return tarjetaVencido(it);
    if (vista === 'saldos') return tarjetaSaldo(it);
    if (vista === 'alertas') return tarjetaAlerta(it);
    return null;
  };
  const VACIO = { por_validar: 'No hay pagos por validar.', en_caja: 'Nada pendiente en caja.', por_resolver: 'Nada por resolver.', vencidos: 'No hay apartados vencidos con dinero.', saldos: 'Ninguna clienta tiene saldo a favor.', alertas: 'Sin alertas abiertas.', historial: 'Sin pagos en esas fechas.' };
  return (
    <>
      <Hoja titulo="Pagos de apartados" sub="Validar, caja, por resolver y saldos" onClose={onClose} datos="bandeja"
        pie={<button type="button" className="pa-btn ghost" onClick={() => setModal({ tipo: 'reportar' })}>Registrar pago de otro folio</button>}>
        <div className="pa-tabs" role="tablist" aria-label="Bandeja de pagos">
          {VISTAS.map(([v, t]) => {
            const n = cuentaVista(cont, v);
            return <button key={v} type="button" role="tab" aria-selected={vista === v} className={vista === v ? 'on' : ''} onClick={() => setVista(v)}>{t}{n > 0 ? <b>{n}</b> : null}</button>;
          })}
        </div>
        <div className="pa-body">
          {vista === 'historial' && (
            <div className="pa-fila" style={{ marginBottom: 12 }}>
              <input className="pa-in" type="date" aria-label="Desde" value={desde} max={hasta} onChange={e => setDesde(e.target.value)} />
              <input className="pa-in" type="date" aria-label="Hasta" value={hasta} min={desde} max={hoyMx()} onChange={e => setHasta(e.target.value)} />
              <button type="button" className="pa-btn ghost" style={{ flex: '0 0 auto' }} onClick={() => cargar('historial')}>Ver</button>
            </div>
          )}
          {vista === 'por_resolver' && <div className="pa-gris" style={{ marginBottom: 10 }}>Compara con lo que se cobró en caja antes de decidir. Nada de esto se registra solo.</div>}
          {error && <div className="pa-note bad" role="alert">{error}</div>}
          {items === null && !error && <div className="pa-vacio">Cargando…</div>}
          {items && items.length === 0 && !error && <div className="pa-vacio">{VACIO[vista]}</div>}
          {items && items.map(pinta)}
        </div>
      </Hoja>
      {modal && modal.tipo === 'validar' && <ModalValidarPago api={api} pago={modal.pago} showToast={showToast} onClose={() => setModal(null)} onHecho={recargar} />}
      {modal && modal.tipo === 'detalle' && <ModalDetallePago api={api} pago={modal.pago} showToast={showToast} onClose={() => setModal(null)} onHecho={recargar} />}
      {modal && modal.tipo === 'accion' && <ModalAccion api={api} item={modal.item} accion={modal.accion} showToast={showToast} onClose={() => setModal(null)} onHecho={recargar} />}
      {modal && modal.tipo === 'reportar' && <ModalReportarPago api={api} apartado={{}} puedeValidar={!!(cont && cont.puede_validar)} showToast={showToast} onClose={() => setModal(null)} onHecho={recargar} />}
    </>
  );
}
